/**
 * OmniLeagues — Website Settings (Option B)
 * Card-grid overview + right-side Sheet per section.
 * Phase 1: Score Ticker only. Placeholder cards for upcoming sections.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WebsiteBlockPreview from './WebsiteBlockPreview';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Monitor, BarChart2, CalendarDays, AlignLeft, Image, Newspaper,
  Globe, Loader2, Save, RefreshCw, Copy, Check, ChevronRight,
  CheckCircle2, AlertTriangle, Clock, Settings2, Megaphone, X, Plus,
  Trophy, Tv, Star, ImagePlus, Users, Download, Upload,
  Bell, GripVertical, Trash2, Pencil, ChevronDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Ticker defaults (must match backend DEFAULT_TICKER) ────────────────── */
const DEFAULT_TICKER = {
  days_back: 5,
  days_ahead: 2,
  show_league_section: true,
  off_season_message: 'We are excited for our {SEASON} Season!',
  scroll_duration_normal: 240,
  scroll_duration_offseason: 60,
  win_color: '#e87722',
  ticker_bg: '#0d0d1a',
  // Round 2 — typography
  font_family: 'Inter, system-ui, sans-serif',
  font_size: 14,
  font_color: '#ffffff',
  team_separator_color: '#3a3a4d',
};

/* ── Sponsor defaults ───────────────────────────────────────────────────── */
const DEFAULT_SPONSORS = {
  refresh_rate_seconds: 5,
  taglines: [],
};

/* ── Hero defaults ──────────────────────────────────────────────────────── */
/* ── Sponsorship Packages defaults (OL-editable; backend fills real values) ─ */
const DEFAULT_SPONSOR_PACKAGES = {
  heading: 'SPONSORSHIP PACKAGES',
  intro: '',
  packages: [],
  footnote: '',
  tax_note: '',
  cta_email: '',
  cta_url: '',
  cta_label: 'BECOME A SPONSOR',
};

const DEFAULT_DONATION = {
  enabled: true,
  heading: 'Become a Sponsor',
  button_label: 'Become a Sponsor',
  donate_url: '',
  note: 'South Wake Sabres is a tax exempt 501(c)(3) charitable organization.',
};

const DEFAULT_HERO = {
  slide_count: 3,
  media_tag: 'hero-slide',
  autoplay_seconds: 6,
  show_overlay_text: true,
  slides: [],
};

/* ── New phases 4d-4i defaults (must mirror backend) ────────────────────── */
const DEFAULT_UPCOMING_GAMES = {
  active_sports:    ['basketball', 'baseball', 'volleyball'],
  cards_per_view:   4,
  window:           'season',
  days_back:        7,
};

const DEFAULT_HOME_SECTIONS = {
  featured_stories: { badge_text: 'Latest News', read_label: 'Read the Story', more_label: 'Additional News', more_url: '/news/' },
  our_sports:       { count: 3, post_tag: 'our-sports', media_tag_template: 'sports-{sport}' },
  championships:    { count: 4, media_tag_prefix: 'champion', hide_if_empty: true },
};

/* ── Explore More (home quick-links) defaults — must match backend ──────── */
const DEFAULT_EXPLORE_MORE = {
  enabled: true,
  title: 'EXPLORE MORE',
  subtitle: 'Quick links to everything Sabres',
  cards: [
    { icon: '📅', title: 'SCHEDULES', body: 'View upcoming games, tournaments, and practice times for all teams.', link_label: 'VIEW SCHEDULES', link_url: '', action: 'schedules_modal' },
    { icon: '🏆', title: 'ROSTERS', body: 'Meet the athletes and coaches behind every Sabres team this season.', link_label: 'SEE ROSTERS', link_url: '/teams/', action: '' },
    { icon: '✍️', title: 'REGISTER', body: 'Sign up for the upcoming season and secure your spot on the team.', link_label: 'REGISTER NOW', link_url: '/register/', action: '' },
  ],
};

const DEFAULT_STATS = [];

const DEFAULT_SABRES_TV = {
  section_title: 'Sabres TV',
  video_count:   6,
  youtube_url:   '',
};

const DEFAULT_JOIN_CTA = {
  title:                   'Join the Sabres Family',
  subtitle:                'Registration is open for the {SEASON} season',
  bg_image_tag:            'join-cta-bg',
  primary_btn_color:       '#e87722',
  secondary_btn_color:     '#0d0d1a',
  primary_btn_link_slug:   'register',
  secondary_btn_link_slug: 'contact',
};

const DEFAULT_NAV = {
  logo_media_tag: 'nav-logo',
  // The OL-managed header menu. Empty means "leave the site alone" - WP falls
  // back to whatever it rendered before, so an org can never lose its nav.
  primary: [],
};

/* ── Popup Notifications defaults (must mirror backend DEFAULT_POPUP_NOTIFICATIONS) ── */
const DEFAULT_POPUP_NOTIFICATIONS = { items: [] };
const DEFAULT_POPUP_COLOR = '#5c0a14'; // theme maroon; per-notice override
const newPopupItem = () => ({
  id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `n-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  enabled: true,
  label: '',
  url: '',
  open_in_new_tab: null, // null = auto (off-site → new tab)
  sport: '',
  starts_at: '',
  ends_at: '',
  button_color: DEFAULT_POPUP_COLOR,
  order: 0,
  history: [],
});

/* ── Register CTA defaults (legacy — kept for one-release backward compat) ── */
const DEFAULT_REGISTER_CTA = {
  enabled: false,
  label: 'Register',
  url: '',
  starts_at: '',
  ends_at: '',
  sport: '',
  season_label: '',
  history: [], // up to 5 prior registrations, archived server-side on URL change
};

/* ── Footer defaults (must mirror backend DEFAULT_FOOTER) ───────────────── */
const DEFAULT_FOOTER = {
  tagline: 'Building Champions, On and Off the Court.',
  address: { line1: '', line2: '', city: '', state: '', zip: '' },
  contact: { email: '', phone: '' },
  social_links: [],
  quick_links: [
    { label: 'Register', slug: 'register' },
    { label: 'Tryouts',  slug: 'tryouts' },
    { label: 'Schedule', slug: 'schedule' },
    { label: 'Contact',  slug: 'contact' },
  ],
  sport_links_mode: 'auto',
  sport_links_custom: [],
  sponsor_strip_enabled: true,
  copyright_text: '© {YEAR} {ORG_NAME}. All rights reserved.',
  legal_links: [
    { label: 'Privacy Policy',   slug: 'privacy' },
    { label: 'Terms of Service', slug: 'terms' },
  ],
  show_powered_by: true,
  powered_by_text: 'Powered by OmniLeagues',
  bg_color:     '#0d0d1a',
  text_color:   '#cbd5e1',
  accent_color: '#e87722',
};

const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'instagram', 'youtube', 'tiktok', 'linkedin'];

/* Convenience: per-row default for stats[] when operator adds a new sport */
const newStatRow = (sport = 'basketball') => ({
  sport,
  label_total:    'Total Wins',
  value_total:    0,
  label_alltime:  'All-Time Championships',
  value_alltime:  0,
  label_years:    'Years Active',
  value_years:    0,
  label_active:   'Active Teams',
  value_active:   0,
  font_color:     '#e87722',
});

/* Curated typography choices that look good on a dark scrolling marquee.
   Operator picks one — string is sent to WP and applied as `font-family`. */
const FONT_OPTIONS = [
  { label: 'Inter (default)',     value: 'Inter, system-ui, sans-serif' },
  { label: 'Roboto',              value: 'Roboto, system-ui, sans-serif' },
  { label: 'Oswald (condensed)',  value: 'Oswald, Impact, sans-serif' },
  { label: 'Bebas Neue (bold)',   value: '"Bebas Neue", Impact, sans-serif' },
  { label: 'Montserrat',          value: 'Montserrat, system-ui, sans-serif' },
  { label: 'Anton (display)',     value: 'Anton, Impact, sans-serif' },
  { label: 'System UI',           value: 'system-ui, sans-serif' },
  { label: 'Serif (Georgia)',     value: 'Georgia, serif' },
];

/* ── Leadership defaults (must mirror backend DEFAULT_LEADERSHIP) ───────── */
const DEFAULT_LEADERSHIP = {
  source: 'ol',
  board: [],
  steering: [],
  basketball: [],
  baseball: [],
};

/* ── About page defaults (must mirror backend DEFAULT_ABOUT_PAGE) ───────── */
const DEFAULT_ABOUT_PAGE = {
  hero_eyebrow: 'HOME / ABOUT',
  hero_title: 'ABOUT THE SABRES',
  who_we_are: { title: 'WHO WE ARE', body: '' },
  mission: { title: 'OUR MISSION', subtitle: 'What drives everything we do', body: '' },
  statement_of_faith: { enabled: true, title: 'STATEMENT OF FAITH', subtitle: 'What we believe', intro: '', articles: [] },
  affiliations: { title: 'AFFILIATIONS', subtitle: 'Proud members and competitors', items: [] },
  contact: { title: 'GET IN TOUCH', body: '', button_label: 'CONTACT US', button_slug: 'contact' },
};

/* ── Members page defaults (must mirror backend DEFAULT_MEMBERS_PAGE) ───── */
const DEFAULT_MEMBERS_PAGE = {
  hero_eyebrow: 'HOME / MEMBERS',
  hero_title: 'MEMBERS AREA',
  intro_title: 'SABRES MEMBERS',
  intro_body: '',
  cards: [],
};

const MEMBER_ICON_CHOICES = ['lock', 'edit', 'video', 'store', 'book', 'app'];

/* ── Standings/Records defaults (must mirror backend DEFAULT_STANDINGS) ──── */
const DEFAULT_STANDINGS = {
  overall_records_sport: 'basketball',
};

/* ── Permissions defaults (must mirror backend DEFAULT_PERMISSIONS) ──────── */
const DEFAULT_PERMISSIONS = {
  team_can_message: true,
  team_can_manage_roster: false,
  team_can_edit_schedule: false,
};

/* ── Section catalogue ──────────────────────────────────────────────────── */
const SECTION_DEFS = [
  {
    key: 'ticker',
    icon: Monitor,
    iconColor: 'text-amber-400',
    title: 'Score Ticker',
    description: 'Scrolling score bar — data window, scroll speed, colours, typography, off-season message.',
    built: true,
  },
  {
    key: 'sponsors',
    icon: Megaphone,
    iconColor: 'text-orange-400',
    title: 'Sponsor Bar',
    description: 'Carousel refresh rate and the tagline pool WP picks from for each sponsor logo.',
    built: true,
  },
  {
    key: 'sponsor_packages',
    icon: Trophy,
    iconColor: 'text-amber-400',
    title: 'Sponsorship Packages',
    description: 'The /community/sponsorship/ page — level names, prices, benefit lists, footnote, tax note and the "Become a Sponsor" CTA. Fully OL-driven.',
    built: true,
  },
  {
    key: 'upcoming_games',
    icon: CalendarDays,
    iconColor: 'text-emerald-400',
    title: 'Upcoming Games',
    description: 'Multi-sport schedule slider — active sports list, level tabs, sport-badge toggle.',
    built: true,
  },
  {
    key: 'home_sections',
    icon: Star,
    iconColor: 'text-yellow-400',
    title: 'Featured Content',
    description: 'Featured Stories · Our Sports · Championship Row — counts, tags, auto-hide rules.',
    built: true,
  },
  {
    key: 'explore_more',
    icon: Megaphone,
    iconColor: 'text-orange-400',
    title: 'Explore More (Quick Links)',
    description: 'Homepage quick-link cards — title, subtitle, and each card\u2019s icon/title/body/link. A card can open the Schedules pop-up instead of a page.',
    built: true,
  },
  {
    key: 'stats',
    icon: BarChart2,
    iconColor: 'text-blue-400',
    title: 'Stats Counter',
    description: 'One row per sport — wins / championships / years active / active teams + colour per sport.',
    built: true,
  },
  {
    key: 'sabres_tv',
    icon: Tv,
    iconColor: 'text-red-400',
    title: 'Sabres TV',
    description: 'Section title, video count, and the YouTube channel WP pulls thumbnails from.',
    built: true,
  },
  {
    key: 'nav',
    icon: ImagePlus,
    iconColor: 'text-indigo-400',
    title: 'Header Navigation',
    description: 'The header menu items and their order, plus the Media-Library tag WP uses for the logo.',
    built: true,
  },
  {
    key: 'footer',
    icon: AlignLeft,
    iconColor: 'text-purple-400',
    title: 'Footer',
    description: 'Tagline, address, contact, social, quick & sport links, sponsor strip, copyright, legal & branding.',
    built: true,
  },
  {
    key: 'leadership',
    icon: Users,
    iconColor: 'text-teal-400',
    title: 'Leadership & Staff',
    description: 'About-page Board of Directors, Steering Committee, and Basketball / Baseball coaching staff — every name & role editable here. OL is the single source of truth.',
    built: true,
  },
  {
    key: 'about_page',
    icon: Newspaper,
    iconColor: 'text-teal-300',
    title: 'About Page',
    description: 'The public About page — Who We Are, Our Mission, Statement of Faith, Affiliations, and the Contact CTA. Fully OL-driven (no hardcoding).',
    built: true,
  },
  {
    key: 'donation',
    icon: Globe,
    iconColor: 'text-indigo-300',
    title: 'Donations (QR)',
    description: 'The "Make a Donation" button + auto-generated QR code shown on the Sponsors & Packages pages. Set the donation link — the QR is generated from it.',
    built: true,
  },
  {
    key: 'members_page',
    icon: Users,
    iconColor: 'text-cyan-300',
    title: 'Members Page',
    description: 'The Members hub — intro copy plus every member card (Login, Register, Videos, Store, Resources, App) with its link.',
    built: true,
  },
  {
    key: 'standings',
    icon: Trophy,
    iconColor: 'text-amber-400',
    title: 'Records Default Sport',
    description: 'Statistics page — which sport\u2019s historical records show first. Visitors can still switch between Basketball / Baseball / Volleyball tabs.',
    built: true,
  },
  {
    key: 'permissions',
    icon: Users,
    iconColor: 'text-rose-400',
    title: 'Team Permissions',
    description: 'Org-wide toggles for what team admins may do — send messages, manage their roster, edit their schedule.',
    built: true,
  },
  // NOTE: a legacy "News & Posts" section card used to live here as a
  // {built:false} placeholder. The real implementation shipped as a
  // dedicated tab (Tech Settings → Pages & News, backed by
  // WebsiteContentPanel.js + routes/wp_pages.py + routes/wp_posts.py),
  // so the placeholder has been removed to avoid two surfaces with
  // overlapping names.
];

/* ── Reusable sub-components ────────────────────────────────────────────── */

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus:outline-none ${checked ? 'bg-amber-500' : 'bg-slate-600'}`}
      data-testid={testId}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

/* Free-typing number input. The old inline `Math.max(min, Math.min(max, …))`
   on every keystroke made it impossible to type a lower number (typing "5" over
   "30" instantly snapped back to the min). This lets the user type freely — no
   clamping while typing — and only normalises on blur. `floor` guards against a
   broken 0/empty value; there is intentionally no upper cap. */
function NumberInput({ value, onCommit, floor = 1, className, testId, ...props }) {
  const [text, setText] = React.useState(value == null ? '' : String(value));
  React.useEffect(() => { setText(value == null ? '' : String(value)); }, [value]);
  return (
    <Input
      type="number"
      value={text}
      className={className}
      data-testid={testId}
      onChange={e => {
        const v = e.target.value;
        setText(v); // never clamp here — allow low numbers and a temporarily empty field
        if (v !== '' && v !== '-') {
          const n = parseInt(v, 10);
          if (!isNaN(n)) onCommit(n);
        }
      }}
      onBlur={e => {
        const raw = e.target.value;
        let n = parseInt(raw, 10);
        if (raw === '' || isNaN(n)) n = floor;
        if (n < floor) n = floor;
        setText(String(n));
        onCommit(n);
      }}
      {...props}
    />
  );
}

function ColorField({ label, value, onChange, testId, hint }) {
  const pickerId = `cp-${testId}`;
  return (
    <div>
      <Label className="text-xs text-slate-300">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <div
          className="h-8 w-8 rounded cursor-pointer border border-slate-600 flex-shrink-0 transition-transform hover:scale-110"
          style={{ backgroundColor: value }}
          onClick={() => document.getElementById(pickerId)?.click()}
          title="Open colour picker"
        />
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm font-mono text-white"
          placeholder="#rrggbb"
          data-testid={testId}
        />
        <input id={pickerId} type="color" value={value}
          onChange={e => onChange(e.target.value)} className="sr-only" />
      </div>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

/* ── Team permissions toggles ───────────────────────────────────────────── */
function PermissionsSheetBody({ perms, setP }) {
  const items = [
    { k: 'team_can_message', label: 'Team admins can send messages' },
    { k: 'team_can_manage_roster', label: 'Team admins can manage their roster' },
    { k: 'team_can_edit_schedule', label: 'Team admins can edit their schedule' },
  ];
  return (
    <div className="space-y-3" data-testid="permissions-sheet-body">
      <p className="text-[11px] text-slate-500">Org-wide defaults. Applies to every team admin unless overridden per program.</p>
      {items.map(it => (
        <div key={it.k} className="flex items-center justify-between p-3 bg-slate-800/60 rounded-lg border border-slate-700">
          <Label className="text-xs text-slate-200">{it.label}</Label>
          <Toggle checked={!!perms[it.k]} onChange={v => setP(it.k, v)} testId={`perm-${it.k}`} />
        </div>
      ))}
    </div>
  );
}

/* ── Records default-sport selector (one OL control) ────────────────────── */
function StandingsSheetBody({ standings, setStd }) {
  const sports = [
    { v: 'basketball', label: 'Basketball' },
    { v: 'baseball',   label: 'Baseball' },
    { v: 'volleyball', label: 'Volleyball' },
  ];
  const current = (standings?.overall_records_sport || 'basketball').toLowerCase();
  return (
    <div className="space-y-5" data-testid="standings-sheet-body">
      <div>
        <Label className="text-xs text-slate-200">Default sport for Historical Records</Label>
        <p className="text-[10px] text-slate-500 mt-0.5 mb-2">
          Sets which sport's records open first on the Statistics page. Visitors can still
          switch to any other sport that has data via the on-page tabs.
        </p>
        <div className="flex flex-col gap-2">
          {sports.map(s => (
            <button key={s.v} type="button"
              onClick={() => setStd('overall_records_sport', s.v)}
              className={`text-left px-3 py-2 rounded border text-sm transition-colors ${current === s.v ? 'bg-amber-600/20 border-amber-500 text-amber-300' : 'bg-slate-900 border-slate-600 text-slate-300 hover:border-slate-400'}`}
              data-testid={`standings-sport-${s.v}`}>
              {s.label}{current === s.v ? '  •  default' : ''}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Leadership editor (Board / Steering / Coaching staff) ──────────────── */
function LeadershipSheetBody({ lead, setL }) {
  const groups = [
    { key: 'board',      label: 'Board of Directors',        hasTeam: false, hasSport: false },
    { key: 'steering',   label: 'Steering Committee',        hasTeam: false, hasSport: true  },
    { key: 'basketball', label: 'Basketball Coaching Staff', hasTeam: true,  hasSport: false },
    { key: 'baseball',   label: 'Baseball Coaching Staff',   hasTeam: true,  hasSport: false },
  ];
  // Steering sport options come from the org's active sports (SOT) — dynamic,
  // no hardcoded list. "All sports" = shows under every sport tab (e.g. the AD).
  const sportOpts = [{ v: '', l: 'All sports' },
    ...((Array.isArray(lead.steering_sports) ? lead.steering_sports : [])
      .map(s => ({ v: s.key, l: s.label })))];
  const rows = (k) => (Array.isArray(lead[k]) ? lead[k] : []);
  const setRow = (k, i, field, val) => {
    const next = [...rows(k)];
    next[i] = { ...next[i], [field]: val };
    setL(k, next);
  };
  const addRow = (k, hasTeam) =>
    setL(k, [...rows(k), hasTeam ? { team: '', role: '', name: '', bio: '' } : { role: '', name: '', bio: '' }]);
  const removeRow = (k, i) => { const next = [...rows(k)]; next.splice(i, 1); setL(k, next); };

  /* ── CSV: pre-filled template download + bulk import ─────────────────── */
  const [csvMsg, setCsvMsg] = useState(null);
  const fileRef = React.useRef(null);
  const CSV_HEADERS = ['group', 'team', 'role', 'name', 'bio'];

  const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };

  const downloadTemplate = () => {
    const lines = [CSV_HEADERS.join(',')];
    let total = 0;
    groups.forEach(g => rows(g.key).forEach(r => {
      total++;
      lines.push([g.key, g.hasTeam ? (r.team || '') : '', r.role || '', r.name || '', r.bio || ''].map(csvEscape).join(','));
    }));
    if (total === 0) {
      // No data yet → include one example per group so the format is clear.
      lines.push(['board', '', 'President', 'Jane Doe', 'Optional short bio'].map(csvEscape).join(','));
      lines.push(['steering', '', 'Athletic Director', 'John Smith', ''].map(csvEscape).join(','));
      lines.push(['basketball', 'Varsity Boys', 'Head Coach', 'Coach Name', ''].map(csvEscape).join(','));
      lines.push(['baseball', 'Varsity', 'Head Coach', 'Coach Name', ''].map(csvEscape).join(','));
    }
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leadership-staff-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const out = []; let field = ''; let row = []; let inQ = false; let i = 0;
    text = text.replace(/^\uFEFF/, '');
    const pushField = () => { row.push(field); field = ''; };
    const pushRow = () => { pushField(); if (row.length > 1 || row[0] !== '') out.push(row); row = []; };
    while (i < text.length) {
      const c = text[i];
      if (inQ) {
        if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } inQ = false; i++; continue; }
        field += c; i++; continue;
      }
      if (c === '"') { inQ = true; i++; continue; }
      if (c === ',') { pushField(); i++; continue; }
      if (c === '\r') { i++; continue; }
      if (c === '\n') { pushRow(); i++; continue; }
      field += c; i++;
    }
    if (field !== '' || row.length) pushRow();
    return out;
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result));
        if (parsed.length < 2) { setCsvMsg({ ok: false, text: 'CSV has no data rows.' }); return; }
        const header = parsed[0].map(h => h.trim().toLowerCase());
        const gi = header.indexOf('group'), ti = header.indexOf('team'), ri = header.indexOf('role'), ni = header.indexOf('name'), bi = header.indexOf('bio');
        if (gi === -1 || ni === -1) { setCsvMsg({ ok: false, text: 'CSV must include "group" and "name" columns.' }); return; }
        const valid = new Set(groups.map(g => g.key));
        const buckets = { board: [], steering: [], basketball: [], baseball: [] };
        const seen = new Set();
        let imported = 0, skipped = 0;
        for (let r = 1; r < parsed.length; r++) {
          const cells = parsed[r];
          const grp = (cells[gi] || '').trim().toLowerCase();
          const name = (cells[ni] || '').trim();
          if (!valid.has(grp) || !name) { if (cells.join('').trim()) skipped++; continue; }
          const g = groups.find(x => x.key === grp);
          const rec = { role: (ri > -1 ? cells[ri] : '') || '', name, bio: (bi > -1 ? cells[bi] : '') || '' };
          if (g.hasTeam) rec.team = (ti > -1 ? cells[ti] : '') || '';
          buckets[grp].push(rec); seen.add(grp); imported++;
        }
        if (!imported) { setCsvMsg({ ok: false, text: 'No valid rows found. Check the "group" values (board / steering / basketball / baseball).' }); return; }
        // Replace only the groups present in the uploaded file.
        seen.forEach(grp => setL(grp, buckets[grp]));
        setCsvMsg({ ok: true, text: `Imported ${imported} row(s) into ${seen.size} group(s)${skipped ? `, skipped ${skipped}` : ''}. Review below, then Save.` });
      } catch {
        setCsvMsg({ ok: false, text: 'Could not read the CSV. Please check the format.' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-7" data-testid="leadership-sheet-body">
      {/* Bulk CSV tools */}
      <div className="p-3 bg-slate-800/60 rounded-lg border border-slate-700 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-xs text-slate-200">Bulk Edit via CSV</Label>
            <p className="text-[10px] text-slate-500 mt-0.5">Download a template pre-filled with the current staff, edit it in Excel/Sheets, then re-upload to replace those groups.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-[11px] border-slate-600 text-slate-300"
              onClick={downloadTemplate} data-testid="leadership-csv-download">
              <Download className="h-3.5 w-3.5 mr-1" /> Template
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-600/60 text-amber-300"
              onClick={() => fileRef.current && fileRef.current.click()} data-testid="leadership-csv-import">
              <Upload className="h-3.5 w-3.5 mr-1" /> Import CSV
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={handleImport} data-testid="leadership-csv-file-input" />
          </div>
        </div>
        {csvMsg && (
          <p className={`text-[11px] ${csvMsg.ok ? 'text-emerald-400' : 'text-red-400'}`} data-testid="leadership-csv-message">
            {csvMsg.text}
          </p>
        )}
      </div>

      {groups.map(g => (
        <div key={g.key} data-testid={`leadership-group-${g.key}`}>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold text-slate-200 uppercase tracking-wide">{g.label}</Label>
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300"
              onClick={() => addRow(g.key, g.hasTeam)} data-testid={`leadership-add-${g.key}`}>
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          {rows(g.key).length === 0 && <p className="text-[10px] text-slate-600 mb-2">No entries yet.</p>}
          <div className="space-y-2">
            {rows(g.key).map((r, i) => (
              <div key={i} className="p-2 bg-slate-800/50 rounded border border-slate-700 space-y-1.5" data-testid={`leadership-row-${g.key}-${i}`}>
                <div className="flex items-center gap-1.5">
                  <Input value={r.name || ''} placeholder="Full name"
                    onChange={e => setRow(g.key, i, 'name', e.target.value)}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1"
                    data-testid={`leadership-${g.key}-name-${i}`} />
                  <button type="button" onClick={() => removeRow(g.key, i)}
                    className="text-slate-500 hover:text-red-400 flex-shrink-0 p-1"
                    data-testid={`leadership-remove-${g.key}-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex items-center gap-1.5">
                  {g.hasTeam && (
                    <Input value={r.team || ''} placeholder="Team / group"
                      onChange={e => setRow(g.key, i, 'team', e.target.value)}
                      className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1"
                      data-testid={`leadership-${g.key}-team-${i}`} />
                  )}
                  <Input value={r.role || ''} placeholder="Role / title"
                    onChange={e => setRow(g.key, i, 'role', e.target.value)}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1"
                    data-testid={`leadership-${g.key}-role-${i}`} />
                  {g.hasSport && (
                    <select value={r.sport || ''}
                      onChange={e => setRow(g.key, i, 'sport', e.target.value)}
                      className="bg-slate-900 border border-slate-600 rounded h-7 text-xs text-white px-1.5 flex-shrink-0"
                      title="Which sport tab this member shows under on the About page"
                      data-testid={`leadership-${g.key}-sport-${i}`}>
                      {sportOpts.map(o => <option key={o.v || 'all'} value={o.v}>{o.l}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── About page editor ──────────────────────────────────────────────────── */
const taCls = 'w-full bg-slate-900 border border-slate-600 rounded-md text-xs text-white p-2 min-h-[80px]';
const inCls = 'bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white';

function AboutPageSheetBody({ val, setVal }) {
  const updN = (sec, field, v) => setVal({ ...val, [sec]: { ...(val[sec] || {}), [field]: v } });
  const sof = val.statement_of_faith || {};
  const arts = Array.isArray(sof.articles) ? sof.articles : [];
  const setArt = (i, f, v) => { const n = [...arts]; n[i] = { ...n[i], [f]: v }; updN('statement_of_faith', 'articles', n); };
  const aff = val.affiliations || {};
  const items = Array.isArray(aff.items) ? aff.items : [];
  const setAff = (i, f, v) => { const n = [...items]; n[i] = { ...n[i], [f]: v }; updN('affiliations', 'items', n); };
  return (
    <div className="space-y-6" data-testid="about-page-sheet-body">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-300">Hero Eyebrow</Label>
          <Input value={val.hero_eyebrow || ''} onChange={e => setVal({ ...val, hero_eyebrow: e.target.value })} className={inCls} data-testid="about-hero-eyebrow" /></div>
        <div><Label className="text-xs text-slate-300">Hero Title</Label>
          <Input value={val.hero_title || ''} onChange={e => setVal({ ...val, hero_title: e.target.value })} className={inCls} data-testid="about-hero-title" /></div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Who We Are</p>
        <Input value={val.who_we_are?.title || ''} onChange={e => updN('who_we_are', 'title', e.target.value)} className={inCls} placeholder="Section title" data-testid="about-wwa-title" />
        <textarea value={val.who_we_are?.body || ''} onChange={e => updN('who_we_are', 'body', e.target.value)} className={`${taCls} mt-2 min-h-[120px]`} placeholder="Body (blank line = new paragraph)" data-testid="about-wwa-body" />
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Our Mission</p>
        <Input value={val.mission?.title || ''} onChange={e => updN('mission', 'title', e.target.value)} className={inCls} placeholder="Title" data-testid="about-mission-title" />
        <Input value={val.mission?.subtitle || ''} onChange={e => updN('mission', 'subtitle', e.target.value)} className={`${inCls} mt-2`} placeholder="Subtitle" data-testid="about-mission-subtitle" />
        <textarea value={val.mission?.body || ''} onChange={e => updN('mission', 'body', e.target.value)} className={`${taCls} mt-2`} placeholder="Mission body" data-testid="about-mission-body" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Statement of Faith</p>
          <Toggle checked={sof.enabled !== false} onChange={v => updN('statement_of_faith', 'enabled', v)} testId="about-sof-enabled" />
        </div>
        <Input value={sof.title || ''} onChange={e => updN('statement_of_faith', 'title', e.target.value)} className={inCls} placeholder="Title" data-testid="about-sof-title" />
        <Input value={sof.subtitle || ''} onChange={e => updN('statement_of_faith', 'subtitle', e.target.value)} className={`${inCls} mt-2`} placeholder="Subtitle" data-testid="about-sof-subtitle" />
        <textarea value={sof.intro || ''} onChange={e => updN('statement_of_faith', 'intro', e.target.value)} className={`${taCls} mt-2`} placeholder="Intro paragraph" data-testid="about-sof-intro" />
        <div className="mt-2 space-y-2">
          {arts.map((a, i) => (
            <div key={i} className="p-2 bg-slate-800/50 rounded border border-slate-700 space-y-1.5" data-testid={`about-sof-article-${i}`}>
              <div className="flex items-center gap-1.5">
                <Input value={a.title || ''} onChange={e => setArt(i, 'title', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1" placeholder="Article title" data-testid={`about-sof-art-title-${i}`} />
                <button type="button" onClick={() => updN('statement_of_faith', 'articles', arts.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 p-1" data-testid={`about-sof-art-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
              </div>
              <textarea value={a.body || ''} onChange={e => setArt(i, 'body', e.target.value)} className={taCls} placeholder="Article text" data-testid={`about-sof-art-body-${i}`} />
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={() => updN('statement_of_faith', 'articles', [...arts, { title: '', body: '' }])} data-testid="about-sof-add"><Plus className="h-3 w-3 mr-1" /> Add Article</Button>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Affiliations</p>
        <Input value={aff.title || ''} onChange={e => updN('affiliations', 'title', e.target.value)} className={inCls} placeholder="Title" data-testid="about-aff-title" />
        <Input value={aff.subtitle || ''} onChange={e => updN('affiliations', 'subtitle', e.target.value)} className={`${inCls} mt-2`} placeholder="Subtitle" data-testid="about-aff-subtitle" />
        <div className="mt-2 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={`about-aff-item-${i}`}>
              <Input value={it.name || ''} onChange={e => setAff(i, 'name', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white w-1/3" placeholder="Name" data-testid={`about-aff-name-${i}`} />
              <Input value={it.desc || ''} onChange={e => setAff(i, 'desc', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1" placeholder="Description" data-testid={`about-aff-desc-${i}`} />
              <button type="button" onClick={() => updN('affiliations', 'items', items.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 p-1" data-testid={`about-aff-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={() => updN('affiliations', 'items', [...items, { name: '', desc: '' }])} data-testid="about-aff-add"><Plus className="h-3 w-3 mr-1" /> Add Affiliation</Button>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Contact CTA</p>
        <Input value={val.contact?.title || ''} onChange={e => updN('contact', 'title', e.target.value)} className={inCls} placeholder="Title" data-testid="about-contact-title" />
        <textarea value={val.contact?.body || ''} onChange={e => updN('contact', 'body', e.target.value)} className={`${taCls} mt-2`} placeholder="Body" data-testid="about-contact-body" />
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><Label className="text-xs text-slate-300">Button Label</Label><Input value={val.contact?.button_label || ''} onChange={e => updN('contact', 'button_label', e.target.value)} className={inCls} data-testid="about-contact-btn-label" /></div>
          <div><Label className="text-xs text-slate-300">Button Page Slug</Label><Input value={val.contact?.button_slug || ''} onChange={e => updN('contact', 'button_slug', e.target.value)} className={`${inCls} font-mono`} placeholder="contact" data-testid="about-contact-btn-slug" /></div>
        </div>
      </div>
    </div>
  );
}

/* ── Members page editor ────────────────────────────────────────────────── */
function MembersPageSheetBody({ val, setVal }) {
  const cards = Array.isArray(val.cards) ? val.cards : [];
  const setCard = (i, f, v) => { const n = [...cards]; n[i] = { ...n[i], [f]: v }; setVal({ ...val, cards: n }); };
  return (
    <div className="space-y-6" data-testid="members-page-sheet-body">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-300">Hero Eyebrow</Label><Input value={val.hero_eyebrow || ''} onChange={e => setVal({ ...val, hero_eyebrow: e.target.value })} className={inCls} data-testid="members-hero-eyebrow" /></div>
        <div><Label className="text-xs text-slate-300">Hero Title</Label><Input value={val.hero_title || ''} onChange={e => setVal({ ...val, hero_title: e.target.value })} className={inCls} data-testid="members-hero-title" /></div>
      </div>
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Intro</p>
        <Input value={val.intro_title || ''} onChange={e => setVal({ ...val, intro_title: e.target.value })} className={inCls} placeholder="Intro title" data-testid="members-intro-title" />
        <textarea value={val.intro_body || ''} onChange={e => setVal({ ...val, intro_body: e.target.value })} className={`${taCls} mt-2`} placeholder="Intro body" data-testid="members-intro-body" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Member Cards</p>
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={() => setVal({ ...val, cards: [...cards, { icon: 'app', title: '', body: '', link_label: '', link_url: '' }] })} data-testid="members-card-add"><Plus className="h-3 w-3 mr-1" /> Add Card</Button>
        </div>
        <div className="space-y-3">
          {cards.map((c, i) => (
            <div key={i} className="p-2.5 bg-slate-800/50 rounded border border-slate-700 space-y-1.5" data-testid={`members-card-${i}`}>
              <div className="flex items-center gap-1.5">
                <select value={c.icon || 'app'} onChange={e => setCard(i, 'icon', e.target.value)} className="bg-slate-900 border border-slate-600 rounded h-7 text-xs text-white px-1" data-testid={`members-card-icon-${i}`}>
                  {MEMBER_ICON_CHOICES.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <Input value={c.title || ''} onChange={e => setCard(i, 'title', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1" placeholder="Card title" data-testid={`members-card-title-${i}`} />
                <button type="button" onClick={() => setVal({ ...val, cards: cards.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-red-400 p-1" data-testid={`members-card-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
              </div>
              <textarea value={c.body || ''} onChange={e => setCard(i, 'body', e.target.value)} className={taCls} placeholder="Card body" data-testid={`members-card-body-${i}`} />
              <div className="flex items-center gap-1.5">
                <Input value={c.link_label || ''} onChange={e => setCard(i, 'link_label', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white w-1/3" placeholder="Link label" data-testid={`members-card-link-label-${i}`} />
                <Input value={c.link_url || ''} onChange={e => setCard(i, 'link_url', e.target.value)} className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1 font-mono" placeholder="/members/videos/ or https://…" data-testid={`members-card-link-url-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Section card ───────────────────────────────────────────────────────── */
function SectionCard({ def, configured, lastSaved, onEdit, data }) {
  const Icon = def.icon;
  return (
    <Card
      className="bg-slate-800/70 border-slate-700 hover:border-slate-500 transition-colors group"
      data-testid={`website-card-${def.key}`}
    >
      <CardContent className="p-4 flex flex-col h-full min-h-[160px]">
        {/* Header row */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 flex-shrink-0 ${def.iconColor}`} />
            <span className="text-sm font-semibold text-white">{def.title}</span>
          </div>
          {def.built ? (
            configured
              ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border">Configured</Badge>
              : <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border-amber-500/30 border">Default</Badge>
          ) : (
            <Badge className="text-[10px] bg-slate-700 text-slate-400 border-slate-600 border">Coming Soon</Badge>
          )}
        </div>

        {/* Live visual hint — reflects the block's current OL settings (never a
            stale screenshot; updates the moment the block is edited/saved). */}
        {def.built && <div className="mb-2"><WebsiteBlockPreview kind={def.key} data={data} /></div>}

        {/* Description */}
        <p className="text-[11px] text-slate-400 leading-relaxed flex-1 mb-3">
          {def.description}
        </p>

        {/* Footer row */}
        <div className="flex items-center justify-between">
          {lastSaved ? (
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(lastSaved).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-[10px] text-slate-600">Not yet saved</span>
          )}

          {def.built ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-slate-600 text-slate-200 hover:bg-slate-700 group-hover:border-amber-500/50 group-hover:text-amber-300"
              onClick={() => onEdit(def.key)}
              data-testid={`website-edit-${def.key}`}
            >
              Edit <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-600 cursor-not-allowed" disabled>
              Edit <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Ticker sheet content ───────────────────────────────────────────────── */
function TickerSheetBody({ ticker, setT }) {
  return (
    <div className="space-y-6">

      {/* Data window */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Data Window</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Days Back</Label>
            <NumberInput value={ticker.days_back}
              onCommit={v => setT('days_back', v)}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              testId="ticker-days-back" />
            <p className="text-[10px] text-slate-500 mt-0.5">Show results from last N days</p>
          </div>
          <div>
            <Label className="text-xs text-slate-300">Days Ahead</Label>
            <NumberInput value={ticker.days_ahead}
              onCommit={v => setT('days_ahead', v)}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              testId="ticker-days-ahead" />
            <p className="text-[10px] text-slate-500 mt-0.5">Show upcoming games in the next N days</p>
          </div>
        </div>
      </div>

      {/* Scroll speed */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Scroll Speed</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">In-Season (seconds)</Label>
            <NumberInput value={ticker.scroll_duration_normal}
              onCommit={v => setT('scroll_duration_normal', v)}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              testId="ticker-scroll-normal" />
            <p className="text-[10px] text-slate-500 mt-0.5">Lower = faster. Default 240s</p>
          </div>
          <div>
            <Label className="text-xs text-slate-300">Off-Season (seconds)</Label>
            <NumberInput value={ticker.scroll_duration_offseason}
              onCommit={v => setT('scroll_duration_offseason', v)}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              testId="ticker-scroll-offseason" />
            <p className="text-[10px] text-slate-500 mt-0.5">Default 60s (4× faster)</p>
          </div>
        </div>
      </div>

      {/* Colours */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Colours</p>
        <div className="grid grid-cols-2 gap-3">
          <ColorField label="Win Highlight" value={ticker.win_color}
            onChange={v => setT('win_color', v)} testId="ticker-win-color"
            hint="Winning team name + score" />
          <ColorField label="Ticker Background" value={ticker.ticker_bg}
            onChange={v => setT('ticker_bg', v)} testId="ticker-bg-color"
            hint="Dark background behind ticker bar" />
          <ColorField label="Body Text" value={ticker.font_color}
            onChange={v => setT('font_color', v)} testId="ticker-font-color"
            hint="Default colour for all ticker text" />
          <ColorField label="Team Separator" value={ticker.team_separator_color}
            onChange={v => setT('team_separator_color', v)} testId="ticker-separator-color"
            hint="Vertical bar between scores" />
        </div>
      </div>

      {/* Typography (Round 2) */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Typography</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Font Family</Label>
            <select
              value={ticker.font_family}
              onChange={e => setT('font_family', e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-md h-8 text-sm mt-1 text-white px-2"
              data-testid="ticker-font-family"
            >
              {FONT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-500 mt-0.5">WP applies as `font-family` on the ticker root.</p>
          </div>
          <div>
            <Label className="text-xs text-slate-300">Font Size (px)</Label>
            <NumberInput value={ticker.font_size}
              onCommit={v => setT('font_size', v)}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              testId="ticker-font-size" />
            <p className="text-[10px] text-slate-500 mt-0.5">Default 14px.</p>
          </div>
        </div>
        {/* Live preview so the operator sees the effect of their choices */}
        <div
          className="mt-3 p-3 rounded-md border border-slate-700/60"
          style={{
            backgroundColor: ticker.ticker_bg,
            color: ticker.font_color,
            fontFamily: ticker.font_family,
            fontSize: `${ticker.font_size}px`,
          }}
          data-testid="ticker-preview"
        >
          <span>FINAL Hawks </span>
          <span style={{ color: ticker.win_color, fontWeight: 700 }}>Sabres 78</span>
          <span> – Falcons 64 </span>
          <span style={{ color: ticker.team_separator_color, margin: '0 8px' }}>│</span>
          <span>FINAL Eagles </span>
          <span style={{ color: ticker.win_color, fontWeight: 700 }}>Sabres 52</span>
          <span> – Wolves 47</span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1">Live preview — exactly how WP will render.</p>
      </div>

      {/* Sections */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Sections</p>
        <div className="flex items-center justify-between p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
          <div className="pr-4">
            <p className="text-xs font-medium text-slate-200">Show "Around The League" Section</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Scores from other teams in your league. Disable if data unavailable.
            </p>
          </div>
          <Toggle checked={ticker.show_league_section}
            onChange={v => setT('show_league_section', v)}
            testId="ticker-show-league-toggle" />
        </div>
      </div>

      {/* Off-season */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Off-Season</p>
        <div>
          <Label className="text-xs text-slate-300">Off-Season Message</Label>
          <Input value={ticker.off_season_message}
            onChange={e => setT('off_season_message', e.target.value)}
            className="bg-slate-900 border-slate-600 text-sm mt-1 text-white"
            placeholder="We are excited for our {SEASON} Season!"
            data-testid="ticker-offseason-message" />
          <p className="text-[10px] text-slate-500 mt-0.5">
            Use{' '}
            <code className="bg-slate-700/80 px-1 rounded text-amber-300 text-[10px]">{'{SEASON}'}</code>
            {' '}for the auto-calculated season year string (e.g. "2026-2027").
          </p>
        </div>
      </div>

    </div>
  );
}

/* ── Sponsors sheet (Phase 4b) ──────────────────────────────────────────── */
function SponsorsSheetBody({ sponsors, setS }) {
  const [draft, setDraft] = React.useState('');

  const addTagline = () => {
    const t = draft.trim();
    if (!t) return;
    if ((sponsors.taglines || []).includes(t)) { setDraft(''); return; }
    setS('taglines', [...(sponsors.taglines || []), t]);
    setDraft('');
  };
  const removeTagline = idx => {
    const next = [...(sponsors.taglines || [])];
    next.splice(idx, 1);
    setS('taglines', next);
  };

  return (
    <div className="space-y-6">
      {/* Refresh rate */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Carousel Speed</p>
        <Label className="text-xs text-slate-300">Refresh Rate (seconds)</Label>
        <Input type="number" min={2} max={60} value={sponsors.refresh_rate_seconds}
          onChange={e => setS('refresh_rate_seconds', Math.max(2, Math.min(60, parseInt(e.target.value) || 5)))}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          data-testid="sponsors-refresh-rate" />
        <p className="text-[10px] text-slate-500 mt-0.5">How often the sponsor bar advances to the next logo (2–60s). Default 5.</p>
      </div>

      {/* Tagline pool */}
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
          Tagline Pool <span className="text-slate-500 normal-case">— WP picks one at random per logo</span>
        </p>
        <div className="flex gap-2 mb-2">
          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTagline(); } }}
            placeholder="e.g. Proud Sabres Supporter"
            className="bg-slate-900 border-slate-600 h-8 text-sm text-white flex-1"
            data-testid="sponsors-new-tagline" />
          <Button size="sm" onClick={addTagline} disabled={!draft.trim()}
            className="bg-amber-600 hover:bg-amber-700 h-8"
            data-testid="sponsors-add-tagline">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        {(sponsors.taglines || []).length === 0 ? (
          <p className="text-[11px] text-slate-500 italic">
            No taglines yet — WP will fall back to logo alt text. Add at least 3 for variety.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="sponsors-tagline-pool">
            {sponsors.taglines.map((t, idx) => (
              <Badge
                key={`${t}-${idx}`}
                className="bg-slate-700/60 text-slate-200 border-slate-600 hover:bg-slate-700 pr-1 pl-2.5 py-1 text-[11px] font-normal flex items-center gap-1"
                data-testid={`sponsors-tagline-${idx}`}
              >
                <span>{t}</span>
                <button
                  type="button"
                  onClick={() => removeTagline(idx)}
                  className="ml-1 rounded-full hover:bg-red-500/20 p-0.5"
                  aria-label={`Remove ${t}`}
                  data-testid={`sponsors-remove-tagline-${idx}`}
                >
                  <X className="h-3 w-3 text-slate-400 hover:text-red-300" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2">
          {(sponsors.taglines || []).length} tagline{(sponsors.taglines || []).length === 1 ? '' : 's'} in the pool.
          The WP shortcode picks one at random and prints it under the logo.
        </p>
      </div>

      {/* Logo source contract */}
      <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-1">Logo Source</p>
        <p className="text-[11px] text-slate-400 leading-relaxed">
          The carousel pulls logos from <strong className="text-slate-200">Community → Sponsorships</strong> in OmniLeagues.
          Adding/removing sponsors there flows through to both the topbar carousel and the
          /community/sponsors page. No action needed here for logo changes.
        </p>
      </div>
    </div>
  );
}

/* ── Hero sheet (Phase 4c) ──────────────────────────────────────────────── */
function HeroSheetBody({ hero, setH }) {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Carousel Behaviour</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Slide Count</Label>
            <Input type="number" min={1} max={10} value={hero.slide_count}
              onChange={e => setH('slide_count', Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              data-testid="hero-slide-count" />
            <p className="text-[10px] text-slate-500 mt-0.5">WP shows the most-recent N tagged images (1–10).</p>
          </div>
          <div>
            <Label className="text-xs text-slate-300">Autoplay (seconds)</Label>
            <Input type="number" min={3} max={30} value={hero.autoplay_seconds}
              onChange={e => setH('autoplay_seconds', Math.max(3, Math.min(30, parseInt(e.target.value) || 6)))}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              data-testid="hero-autoplay" />
            <p className="text-[10px] text-slate-500 mt-0.5">Time on each slide before rotating.</p>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Overlay Dimness</Label>
            <span className="text-xs font-mono text-amber-300">{hero.overlay_dim ?? 70}%</span>
          </div>
          <input type="range" min={0} max={100} step={5}
            value={hero.overlay_dim ?? 70}
            onChange={e => setH('overlay_dim', Math.max(0, Math.min(100, parseInt(e.target.value))))}
            className="w-full mt-2 accent-amber-500"
            data-testid="hero-overlay-dim" />
          <p className="text-[10px] text-slate-500 mt-0.5">
            Darkness of the gradient over every slide (higher = darker, more readable text). Default 70%.
          </p>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Media Source</p>
        <Label className="text-xs text-slate-300">Media Library Tag</Label>
        <Input value={hero.media_tag}
          onChange={e => setH('media_tag', e.target.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
          placeholder="hero-slide"
          data-testid="hero-media-tag" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          WP filters Media Library attachments whose slug starts with this tag.
          Default: <code className="bg-slate-700/80 px-1 rounded text-amber-300">hero-slide</code>.
        </p>
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <div className="pr-4">
          <p className="text-xs font-medium text-slate-200">Show Overlay Text</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Renders title + subtitle text over each slide. Source per slide is whichever exists first:
            OL slide override (below) → linked WP post → attachment Title/Caption.
          </p>
        </div>
        <Toggle checked={hero.show_overlay_text}
          onChange={v => setH('show_overlay_text', v)}
          testId="hero-overlay-toggle" />
      </div>

      <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-1">Add a Hero Slide</p>
        <ol className="text-[11px] text-slate-400 space-y-1 list-decimal pl-4 leading-relaxed">
          <li>Upload your image to the WordPress Media Library.</li>
          <li>Set its slug to start with <code className="bg-slate-700/80 px-1 rounded text-amber-300">{hero.media_tag}</code>
              {' '}— e.g. <code className="bg-slate-700/80 px-1 rounded text-amber-300">{hero.media_tag}-fall-tryouts</code>.</li>
          <li>Save. WP picks the most-recent {hero.slide_count} matching images on the next page load.</li>
        </ol>
      </div>

      {/* Per-slide overlay text overrides (option b — OL is SoT for slide copy) */}
      {hero.show_overlay_text && (
        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Overlay Text Overrides
            <span className="text-slate-500 normal-case ml-1">— optional, OL-driven slide copy</span>
          </p>
          <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
            Bind a slide to an attachment by typing the slug suffix below
            (e.g. <code className="bg-slate-700/80 px-1 rounded text-amber-300">fall-tryouts</code> matches
            <code className="bg-slate-700/80 px-1 rounded text-amber-300">{hero.media_tag}-fall-tryouts</code>).
            Empty list = WP falls back to the attachment's own Title + Caption.
          </p>
          <div className="space-y-3" data-testid="hero-slides-list">
            {(hero.slides || []).map((s, idx) => (
              <div key={idx} className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-mono">slide #{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...(hero.slides || [])];
                      next.splice(idx, 1);
                      setH('slides', next);
                    }}
                    className="text-[10px] text-red-400 hover:text-red-300"
                    data-testid={`hero-slide-remove-${idx}`}
                  >
                    Remove
                  </button>
                </div>
                <Input
                  value={s.media_slug_match || ''}
                  onChange={e => {
                    const next = [...(hero.slides || [])];
                    next[idx] = { ...s, media_slug_match: e.target.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-') };
                    setH('slides', next);
                  }}
                  className="bg-slate-900 border-slate-600 font-mono h-7 text-xs text-white"
                  placeholder="media slug suffix (e.g. fall-tryouts)"
                  data-testid={`hero-slide-slug-${idx}`}
                />
                <Input
                  value={s.eyebrow || ''}
                  onChange={e => {
                    const next = [...(hero.slides || [])];
                    next[idx] = { ...s, eyebrow: e.target.value };
                    setH('slides', next);
                  }}
                  className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                  placeholder="Eyebrow label (e.g. CHAMPIONS)"
                  data-testid={`hero-slide-eyebrow-${idx}`}
                />
                <Input
                  value={s.title || ''}
                  onChange={e => {
                    const next = [...(hero.slides || [])];
                    next[idx] = { ...s, title: e.target.value };
                    setH('slides', next);
                  }}
                  className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                  placeholder="Title (top overlay)"
                  data-testid={`hero-slide-title-${idx}`}
                />
                <Input
                  value={s.subtitle || ''}
                  onChange={e => {
                    const next = [...(hero.slides || [])];
                    next[idx] = { ...s, subtitle: e.target.value };
                    setH('slides', next);
                  }}
                  className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                  placeholder="Subtitle (bottom overlay)"
                  data-testid={`hero-slide-subtitle-${idx}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={s.cta_text || ''}
                    onChange={e => {
                      const next = [...(hero.slides || [])];
                      next[idx] = { ...s, cta_text: e.target.value };
                      setH('slides', next);
                    }}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                    placeholder="CTA text (optional)"
                    data-testid={`hero-slide-cta-text-${idx}`}
                  />
                  <Input
                    value={s.cta_url || ''}
                    onChange={e => {
                      const next = [...(hero.slides || [])];
                      next[idx] = { ...s, cta_url: e.target.value };
                      setH('slides', next);
                    }}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                    placeholder="CTA URL"
                    data-testid={`hero-slide-cta-url-${idx}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={s.cta2_text || ''}
                    onChange={e => {
                      const next = [...(hero.slides || [])];
                      next[idx] = { ...s, cta2_text: e.target.value };
                      setH('slides', next);
                    }}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                    placeholder="2nd button text (optional)"
                    data-testid={`hero-slide-cta2-text-${idx}`}
                  />
                  <Input
                    value={s.cta2_url || ''}
                    onChange={e => {
                      const next = [...(hero.slides || [])];
                      next[idx] = { ...s, cta2_url: e.target.value };
                      setH('slides', next);
                    }}
                    className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                    placeholder="2nd button URL"
                    data-testid={`hero-slide-cta2-url-${idx}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <Button size="sm"
            className="mt-3 bg-slate-700 hover:bg-slate-600 h-7 text-xs"
            onClick={() => setH('slides', [...(hero.slides || []), { media_slug_match: '', eyebrow: '', title: '', subtitle: '', cta_text: '', cta_url: '', cta2_text: '', cta2_url: '' }])}
            data-testid="hero-slide-add"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Slide Override
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── Upcoming Games sheet (Phase 4d) ────────────────────────────────────── */
const SPORT_CHOICES = [
  { value: 'basketball', label: 'Basketball' },
  { value: 'baseball',   label: 'Baseball' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'soccer',     label: 'Soccer' },
  { value: 'football',   label: 'Football' },
];

function UpcomingGamesSheetBody({ ug, setU }) {
  const toggleSport = (v) => {
    const next = ug.active_sports.includes(v)
      ? ug.active_sports.filter(s => s !== v)
      : [...ug.active_sports, v];
    setU('active_sports', next);
  };
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Active Sports</p>
        <div className="flex flex-wrap gap-2">
          {SPORT_CHOICES.map(s => {
            const on = ug.active_sports.includes(s.value);
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => toggleSport(s.value)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on
                  ? 'bg-emerald-600/20 border-emerald-500/60 text-emerald-200'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                data-testid={`ug-sport-${s.value}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          Slider shows games for every selected sport. Empty selection ⇒ slider stays empty.
        </p>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
          Games Window <span className="text-slate-500 normal-case">— which past games appear</span>
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Show games from</Label>
            <select
              value={ug.window === 'days' ? 'days' : 'season'}
              onChange={e => setU('window', e.target.value)}
              title="Full current season shows every game since the season began (per sport, from OL Sport Dates). Last N days shows only games played in the past N days. Upcoming games always show either way."
              className="w-full mt-1 h-8 rounded-md bg-slate-900 border border-slate-600 text-sm text-white px-2"
              data-testid="ug-window-select">
              <option value="season">Full current season</option>
              <option value="days">Last N days</option>
            </select>
            <p className="text-[10px] text-slate-500 mt-0.5">Upcoming games always show; this sets how far back played games appear.</p>
          </div>
          {ug.window === 'days' && (
            <div>
              <Label className="text-xs text-slate-300">Days back</Label>
              <Input type="number" min={1} max={365} value={ug.days_back ?? 7}
                onChange={e => setU('days_back', Math.max(1, Math.min(365, parseInt(e.target.value) || 7)))}
                title="Number of days of already-played games to include."
                className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
                data-testid="ug-days-back" />
              <p className="text-[10px] text-slate-500 mt-0.5">Only used when “Last N days” is selected.</p>
            </div>
          )}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Display</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Cards Per View</Label>
            <Input type="number" min={1} max={6} value={ug.cards_per_view}
              onChange={e => setU('cards_per_view', Math.max(1, Math.min(6, parseInt(e.target.value) || 4)))}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              data-testid="ug-cards-per-view" />
            <p className="text-[10px] text-slate-500 mt-0.5">How many cards visible at once (1–6).</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Featured Content sheet (Phase 4e — featured stories / our sports / champions) ─ */
function HomeSectionsSheetBody({ hs, setHS }) {
  const updSub = (sub, field, val) => {
    setHS(sub, { ...(hs[sub] || {}), [field]: val });
  };
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Latest News (home page)</p>
        <p className="text-[11px] text-slate-500 mb-2">Always shows the single newest published news post. Blank = default text.</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            ['badge_text', 'Flashing badge', 'Latest News'],
            ['read_label', 'Story link text', 'Read the Story'],
            ['more_label', 'Button text', 'Additional News'],
            ['more_url', 'Button link', '/news/'],
          ].map(([field, label, ph]) => (
            <div key={field}>
              <Label className="text-xs text-slate-300">{label}</Label>
              <Input value={hs.featured_stories?.[field] ?? ''} placeholder={ph}
                onChange={e => updSub('featured_stories', field, e.target.value)}
                className={`bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white${field === 'more_url' ? ' font-mono' : ''}`}
                data-testid={`hs-fs-${field}`} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Our Sports</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Card Count Override</Label>
            <Input type="number" min={1} max={8}
              value={hs.our_sports?.count ?? 3}
              onChange={e => updSub('our_sports', 'count', Math.max(1, Math.min(8, parseInt(e.target.value) || 3)))}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              data-testid="hs-os-count" />
            <p className="text-[10px] text-slate-500 mt-0.5">Defaults to active-sports count if WP supports it.</p>
          </div>
          <div>
            <Label className="text-xs text-slate-300">WP Post Tag</Label>
            <Input value={hs.our_sports?.post_tag ?? 'our-sports'}
              onChange={e => updSub('our_sports', 'post_tag', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
              data-testid="hs-os-tag" />
          </div>
        </div>
        <div className="mt-2">
          <Label className="text-xs text-slate-300">Media Tag Template</Label>
          <Input value={hs.our_sports?.media_tag_template ?? 'sports-{sport}'}
            onChange={e => updSub('our_sports', 'media_tag_template', e.target.value)}
            className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
            data-testid="hs-os-media-template" />
          <p className="text-[10px] text-slate-500 mt-0.5">
            <code className="bg-slate-700/80 px-1 rounded text-amber-300">{'{sport}'}</code> is replaced per active sport. Default <code className="bg-slate-700/80 px-1 rounded text-amber-300">sports-&#123;sport&#125;</code>.
          </p>
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Championship Row</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-300">Picture Count</Label>
            <Input type="number" min={1} max={12}
              value={hs.championships?.count ?? 4}
              onChange={e => updSub('championships', 'count', Math.max(1, Math.min(12, parseInt(e.target.value) || 4)))}
              className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
              data-testid="hs-ch-count" />
          </div>
          <div>
            <Label className="text-xs text-slate-300">Media Slug Prefix</Label>
            <Input value={hs.championships?.media_tag_prefix ?? 'champion'}
              onChange={e => updSub('championships', 'media_tag_prefix', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
              data-testid="hs-ch-prefix" />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between p-2 bg-slate-900/60 rounded-lg border border-slate-700/50">
          <div className="pr-2">
            <p className="text-xs font-medium text-slate-200">Hide Section if Empty</p>
            <p className="text-[10px] text-slate-500 mt-0.5">No matching attachments ⇒ no heading, no row.</p>
          </div>
          <Toggle checked={!!hs.championships?.hide_if_empty}
            onChange={v => updSub('championships', 'hide_if_empty', v)}
            testId="hs-ch-hide-toggle" />
        </div>
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
          Naming convention: <code className="bg-slate-700/80 px-1 rounded text-amber-300">champion-&#123;level&#125;-&#123;sport&#125;-&#123;class&#125;</code>
          {' '}— e.g. <code className="bg-slate-700/80 px-1 rounded text-amber-300">champion-jvb-basketball-state</code>.
          Set the attachment's <strong className="text-slate-200">Title</strong> in WP Media admin to the desired caption.
        </p>
      </div>
    </div>
  );
}

/* ── Stats sheet (Phase 4f — per-sport rows + history) ──────────────────── */
function StatsSheetBody({ rows, setRows, overrides, setOverrides, orgId }) {
  const [computing, setComputing] = React.useState(false);
  const [computeMsg, setComputeMsg] = React.useState(null);
  const [computedMeta, setComputedMeta] = React.useState({}); // sport → meta
  const [breakdowns, setBreakdowns] = React.useState({});     // sport → [{season,wins,losses,source}]
  const [openBreakdown, setOpenBreakdown] = React.useState(null); // sport key
  const [suggestions, setSuggestions] = React.useState({});   // sport → {years_active, years_active_note}

  const updateRow = (idx, field, val) => {
    const next = rows.map((r, i) => i === idx ? { ...r, [field]: val } : r);
    setRows(next);
  };
  const addRow = () => {
    const usedSports = new Set(rows.map(r => r.sport));
    const next = SPORT_CHOICES.find(s => !usedSports.has(s.value))?.value || 'basketball';
    setRows([...rows, newStatRow(next)]);
  };
  const removeRow = (idx) => {
    const next = [...rows];
    next.splice(idx, 1);
    setRows(next);
  };

  // Pull real values from OL (season_archives + ol_teams)
  const computeFromOL = async () => {
    setComputing(true);
    setComputeMsg(null);
    try {
      const { data } = await axios.get(`${API}/api/website-settings/${orgId}/stats/compute`);
      if (data.ok && Array.isArray(data.rows)) {
        const meta = {};
        const breakdownMap = {};
        const suggMap = {};

        // Build a fresh rows array in one pass (single setRows call) to avoid
        // closure clobbering when multiple computed sports merge sequentially.
        let next = [...rows];
        for (const computed of data.rows) {
          meta[computed.sport]         = computed._meta;
          breakdownMap[computed.sport] = computed.breakdown || [];
          suggMap[computed.sport]      = computed.overrides_in_effect || {};
          // eslint-disable-next-line no-unused-vars
          const { _meta, breakdown: _bd, overrides_in_effect: _ov, ...row } = computed;
          const idx = next.findIndex(r => r.sport === row.sport);
          if (idx >= 0) {
            // Replace OL-derived fields ONLY when the backend has a real value
            // (years_active stays null if no override + no archives — preserve operator's typed value)
            next = next.map((rr, i) => i !== idx ? rr : ({
              ...rr,
              value_total:  row.value_total,
              value_active: row.value_active,
              ...(row.value_years != null ? { value_years: row.value_years } : {}),
            }));
          } else {
            // First time we've seen this sport — append a fresh row with auto values
            next.push({
              ...newStatRow(row.sport),
              value_total:  row.value_total,
              value_active: row.value_active,
              value_years:  row.value_years ?? 0,
              font_color:   row.font_color,
            });
          }
        }
        setRows(next);
        setComputedMeta(meta);
        setBreakdowns(breakdownMap);
        setSuggestions(suggMap);
        setComputeMsg({ kind: 'ok', text: `Pulled real wins + active-team counts from OL — review & save.` });
      } else {
        setComputeMsg({ kind: 'err', text: 'Compute returned no rows.' });
      }
    } catch (e) {
      setComputeMsg({ kind: 'err', text: `Compute failed: ${e.response?.data?.detail || e.message}` });
    } finally {
      setComputing(false);
      setTimeout(() => setComputeMsg(null), 6000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Compute-from-OL action bar */}
      <div className="p-3 bg-emerald-900/15 border border-emerald-700/40 rounded-lg flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <RefreshCw className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-[11px] text-emerald-300 uppercase tracking-wide font-semibold">
              Compute from OL data
            </span>
          </div>
          <p className="text-[11px] text-emerald-100/80">
            Pulls <strong>real wins</strong> from <code className="bg-slate-700/60 px-1 rounded text-amber-300">season_archives</code> + finalized live games, and
            <strong> active teams</strong> from the most-recent archive.
            <br />
            <strong className="text-amber-300">Years Active</strong> and <strong className="text-amber-300">Championships</strong> stay <strong>operator-typed</strong> — Compute suggests values inline but never overwrites yours.
          </p>
        </div>
        <Button size="sm"
          onClick={computeFromOL}
          disabled={computing}
          className="bg-emerald-600 hover:bg-emerald-700 h-8"
          data-testid="stats-compute-btn"
        >
          {computing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          {computing ? 'Computing…' : 'Compute from OL'}
        </Button>
      </div>
      {computeMsg && (
        <p className={`text-[11px] ${computeMsg.kind === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}
           data-testid="stats-compute-msg">
          {computeMsg.text}
        </p>
      )}

      {rows.length === 0 && (
        <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
          <p className="text-[11px] text-slate-400">
            No stat rows configured. Click <strong className="text-slate-200">Compute from OL</strong> above to auto-populate from your archives,
            or click <strong className="text-slate-200">Add Sport</strong> below to type values manually.
            WP renders one row per entry; if this list is empty, the home stats section falls back to its current hardcoded values.
          </p>
        </div>
      )}
      {rows.map((r, idx) => (
        <div key={idx} className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50 space-y-3"
             data-testid={`stats-row-${idx}`}>
          <div className="flex items-center justify-between">
            <select value={r.sport}
              onChange={e => updateRow(idx, 'sport', e.target.value)}
              className="bg-slate-900 border border-slate-600 rounded-md h-7 text-xs text-white px-2"
              data-testid={`stats-row-sport-${idx}`}
            >
              {SPORT_CHOICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button type="button" onClick={() => removeRow(idx)}
              className="text-[10px] text-red-400 hover:text-red-300"
              data-testid={`stats-row-remove-${idx}`}>Remove</button>
          </div>

          {/* OL math overrides — type once, OL derives Years Active + Active Teams forever */}
          <div className="p-2 bg-slate-950/40 rounded-md border border-slate-700/40 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-400">Program Start Year</Label>
              <Input type="number" min={1900} max={2100}
                value={overrides[r.sport]?.program_start_year ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  setOverrides({
                    ...overrides,
                    [r.sport]: {
                      ...(overrides[r.sport] || {}),
                      program_start_year: v ? parseInt(v) : null,
                    },
                  });
                }}
                placeholder="e.g. 2008"
                className="bg-slate-900 border-slate-600 h-6 text-[11px] mt-0.5 text-white"
                data-testid={`stats-row-${idx}-program-start`} />
              <p className="text-[9px] text-slate-500 mt-0.5">
                OL → Years Active = current year − this.
              </p>
            </div>
            <div>
              <Label className="text-[10px] text-slate-400">Active Team Count Override</Label>
              <Input type="number" min={0} max={50}
                value={overrides[r.sport]?.team_count ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  setOverrides({
                    ...overrides,
                    [r.sport]: {
                      ...(overrides[r.sport] || {}),
                      team_count: v ? parseInt(v) : null,
                    },
                  });
                }}
                placeholder="leave blank to auto-derive"
                className="bg-slate-900 border-slate-600 h-6 text-[11px] mt-0.5 text-white"
                data-testid={`stats-row-${idx}-team-override`} />
              <p className="text-[9px] text-slate-500 mt-0.5">
                Leave blank to use latest archive count.
              </p>
            </div>
            <div className="col-span-2">
              <Button size="sm"
                onClick={async () => {
                  // Save overrides immediately, then re-compute so the row values reflect them
                  try {
                    await axios.patch(`${API}/api/website-settings/${orgId}`, {
                      stats_overrides: overrides,
                    });
                    const { data } = await axios.get(`${API}/api/website-settings/${orgId}/stats/compute`);
                    if (data.ok) {
                      const computed = (data.rows || []).find(x => x.sport === r.sport);
                      if (computed) {
                        // Batch update — set all three OL-derived fields in ONE setState call
                        // so multiple in-flight closures don't clobber each other.
                        setRows(prev => prev.map((row, i) => i === idx ? {
                          ...row,
                          value_total:  computed.value_total,
                          value_years:  computed.value_years,
                          value_active: computed.value_active,
                        } : row));
                      }
                    }
                  } catch {}
                }}
                className="bg-emerald-700/40 hover:bg-emerald-700/60 h-6 text-[10px] mt-1"
                data-testid={`stats-row-${idx}-apply-overrides`}
              >
                Apply overrides → recompute this row
              </Button>
            </div>
          </div>

          {[
            { v: 'value_total',   l: 'label_total',   ph: 'Total Wins',           src: 'wins_source',  hasBreakdown: true },
            { v: 'value_alltime', l: 'label_alltime', ph: 'Championships',        src: 'champ_source', hasBreakdown: false },
            { v: 'value_years',   l: 'label_years',   ph: 'Years Active',         src: 'years_source', hasBreakdown: false },
            { v: 'value_active',  l: 'label_active',  ph: 'Active Teams',         src: 'teams_source', hasBreakdown: false },
          ].map(({ v, l, ph, src, hasBreakdown }) => (
            <div key={v}>
              <div className="grid grid-cols-[1fr_120px] gap-2">
                <Input value={r[l] ?? ph}
                  onChange={e => updateRow(idx, l, e.target.value)}
                  className="bg-slate-900 border-slate-600 h-7 text-xs text-white"
                  data-testid={`stats-row-${idx}-${l}`} />
                <Input type="number" min={0} value={r[v] ?? 0}
                  onChange={e => updateRow(idx, v, Math.max(0, parseInt(e.target.value) || 0))}
                  className="bg-slate-900 border-slate-600 h-7 text-xs text-white text-right font-mono"
                  data-testid={`stats-row-${idx}-${v}`} />
              </div>
              {computedMeta[r.sport]?.[src] && (
                <p className="text-[9px] text-emerald-400/70 mt-0.5 ml-1 italic">
                  source: {computedMeta[r.sport][src]}
                  {hasBreakdown && (breakdowns[r.sport] || []).length > 0 && (
                    <button type="button"
                      onClick={() => setOpenBreakdown(openBreakdown === r.sport ? null : r.sport)}
                      className="ml-2 text-amber-400 hover:text-amber-300 underline not-italic"
                      data-testid={`stats-row-${idx}-breakdown-toggle`}
                    >
                      {openBreakdown === r.sport ? 'hide' : 'view'} per-season
                    </button>
                  )}
                </p>
              )}
            </div>
          ))}

          {/* Per-season breakdown panel — lets operator spot-check each year against source sheet */}
          {openBreakdown === r.sport && (breakdowns[r.sport] || []).length > 0 && (
            <div className="bg-slate-950/60 rounded-md border border-slate-700/40 p-2 max-h-56 overflow-y-auto">
              <table className="w-full text-[10px] font-mono">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-700/40">
                    <th className="text-left pb-1">Season</th>
                    <th className="text-right pb-1">W</th>
                    <th className="text-right pb-1">L</th>
                    <th className="text-right pb-1">Src</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {breakdowns[r.sport].map((b, bidx) => (
                    <tr key={bidx} className="border-b border-slate-800/40 last:border-0">
                      <td className="py-0.5">{b.season}</td>
                      <td className="text-right text-emerald-400">{b.wins}</td>
                      <td className="text-right text-red-400/80">{b.losses}</td>
                      <td className="text-right text-slate-500">{b.source}</td>
                    </tr>
                  ))}
                  <tr className="font-bold text-slate-100">
                    <td className="py-1 pt-2">TOTAL</td>
                    <td className="text-right text-emerald-300">
                      {breakdowns[r.sport].reduce((s, b) => s + b.wins, 0)}
                    </td>
                    <td className="text-right text-red-400">
                      {breakdowns[r.sport].reduce((s, b) => s + b.losses, 0)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[9px] text-slate-500 mt-1.5 italic">
                Compare these per-season numbers against your source sheet — if a year is wrong,
                re-import that season's archive (or finalise the missing live games).
              </p>
            </div>
          )}

          <ColorField label="Number Colour" value={r.font_color || '#e87722'}
            onChange={v => updateRow(idx, 'font_color', v)}
            testId={`stats-row-${idx}-color`}
            hint="Applied to the four number values in this row" />
        </div>
      ))}

      <Button size="sm"
        className="bg-slate-700 hover:bg-slate-600 h-8 text-xs"
        onClick={addRow}
        data-testid="stats-add-row"
      >
        <Plus className="h-3 w-3 mr-1" /> Add Sport
      </Button>
    </div>
  );
}

/* ── Sabres TV sheet (Phase 4g) ─────────────────────────────────────────── */
function SabresTvSheetBody({ tv, setTv }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs text-slate-300">Section Title</Label>
        <Input value={tv.section_title}
          onChange={e => setTv('section_title', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          placeholder="Sabres TV"
          data-testid="tv-section-title" />
        <p className="text-[10px] text-slate-500 mt-0.5">Heading rendered above the video grid.</p>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Video Count</Label>
        <Input type="number" min={1} max={12} value={tv.video_count}
          onChange={e => setTv('video_count', Math.max(1, Math.min(12, parseInt(e.target.value) || 6)))}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          data-testid="tv-video-count" />
        <p className="text-[10px] text-slate-500 mt-0.5">Most-recent N uploads from the channel.</p>
      </div>
      <div>
        <Label className="text-xs text-slate-300">YouTube Channel / Playlist URL</Label>
        <Input value={tv.youtube_url}
          onChange={e => setTv('youtube_url', e.target.value)}
          className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
          placeholder="https://www.youtube.com/@SouthWakeSabres"
          data-testid="tv-youtube-url" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          WP fetches video thumbnails from this URL. Reuse the same value the org has saved in Settings → Social.
        </p>
      </div>
    </div>
  );
}

/* ── Join CTA sheet (Phase 4h) ──────────────────────────────────────────── */
function JoinCtaSheetBody({ jc, setJ }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs text-slate-300">Title</Label>
        <Input value={jc.title}
          onChange={e => setJ('title', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          data-testid="jc-title" />
      </div>
      <div>
        <Label className="text-xs text-slate-300">Subtitle</Label>
        <Input value={jc.subtitle}
          onChange={e => setJ('subtitle', e.target.value)}
          className="bg-slate-900 border-slate-600 text-sm mt-1 text-white"
          data-testid="jc-subtitle" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          Use <code className="bg-slate-700/80 px-1 rounded text-amber-300">{'{SEASON}'}</code> for the auto-calculated year.
        </p>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Background Image — Media Tag</Label>
        <Input value={jc.bg_image_tag}
          onChange={e => setJ('bg_image_tag', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
          data-testid="jc-bg-tag" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          WP picks the most-recent attachment whose slug starts with this prefix.
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Buttons</p>
        <div className="grid grid-cols-2 gap-3">
          <ColorField label="Register Now (Primary)" value={jc.primary_btn_color}
            onChange={v => setJ('primary_btn_color', v)} testId="jc-primary-color" />
          <ColorField label="Contact Us (Secondary)" value={jc.secondary_btn_color}
            onChange={v => setJ('secondary_btn_color', v)} testId="jc-secondary-color" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs text-slate-300">Primary Quick-Link Slug</Label>
            <Input value={jc.primary_btn_link_slug}
              onChange={e => setJ('primary_btn_link_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
              data-testid="jc-primary-slug" />
          </div>
          <div>
            <Label className="text-xs text-slate-300">Secondary Quick-Link Slug</Label>
            <Input value={jc.secondary_btn_link_slug}
              onChange={e => setJ('secondary_btn_link_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
              data-testid="jc-secondary-slug" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Header-nav item editor ─────────────────────────────────────────────────
 * The header links used to be literal PHP in the WordPress theme, so renaming
 * a menu item needed a developer. This edits them as data; WP reads the pushed
 * structure and falls back to its old markup if we ever send nothing.
 * ────────────────────────────────────────────────────── */
function NavItemRows({ items, onChange, depth = 0, testPrefix }) {
  const commit = (next) => onChange(next);
  const patchAt = (i, patch) => { const n = [...items]; n[i] = { ...n[i], ...patch }; commit(n); };
  const removeAt = (i) => { const n = [...items]; n.splice(i, 1); commit(n); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; commit(n);
  };

  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const kids = Array.isArray(it.children) ? it.children : [];
        const isExternal = !!it.external || /^https?:/i.test(it.url || '');
        return (
          <div key={i}
            className={`rounded-lg border p-2 ${depth ? 'border-slate-700/40 bg-slate-900/40' : 'border-slate-700/60 bg-slate-900/60'}`}
            data-testid={`${testPrefix}-row-${i}`}>
            <div className="flex gap-2 items-center">
              <div className="flex flex-col">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                  className="h-3 leading-none text-slate-500 hover:text-amber-300 disabled:opacity-20"
                  title="Move up" data-testid={`${testPrefix}-up-${i}`}>▲</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                  className="h-3 leading-none text-slate-500 hover:text-amber-300 disabled:opacity-20"
                  title="Move down" data-testid={`${testPrefix}-down-${i}`}>▼</button>
              </div>
              <Input value={it.label || ''}
                onChange={e => patchAt(i, { label: e.target.value })}
                className="bg-slate-900 border-slate-600 h-8 text-xs text-white flex-1"
                placeholder="Label (e.g. Members)"
                data-testid={`${testPrefix}-label-${i}`} />
              {isExternal ? (
                <Input value={it.url || ''}
                  onChange={e => patchAt(i, { url: e.target.value })}
                  className="bg-slate-900 border-slate-600 h-8 text-xs font-mono text-white flex-1"
                  placeholder="https://..."
                  data-testid={`${testPrefix}-url-${i}`} />
              ) : (
                <Input value={it.slug || ''}
                  onChange={e => patchAt(i, { slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="bg-slate-900 border-slate-600 h-8 text-xs font-mono text-white flex-1"
                  placeholder="slug (e.g. members/app)"
                  data-testid={`${testPrefix}-slug-${i}`} />
              )}
              <Button size="sm" variant="ghost"
                className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                onClick={() => removeAt(i)}
                data-testid={`${testPrefix}-del-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 pl-6">
              <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer">
                <input type="checkbox" checked={!!it.members_only}
                  onChange={e => patchAt(i, { members_only: e.target.checked })}
                  data-testid={`${testPrefix}-members-${i}`} />
                Members only
              </label>
              <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer">
                <input type="checkbox" checked={isExternal}
                  onChange={e => patchAt(i, e.target.checked
                    ? { external: true, url: it.url || '', slug: undefined }
                    : { external: false, url: undefined, slug: it.slug || '' })}
                  data-testid={`${testPrefix}-external-${i}`} />
                External link
              </label>
              {depth === 0 && (
                <>
                  <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer"
                    title="Hand this item to the theme's season-aware sport dropdown">
                    <input type="checkbox" checked={!!it.sport}
                      onChange={e => patchAt(i, { sport: e.target.checked
                        ? (it.sport || (it.slug || it.label || '').toLowerCase()) : '' })}
                      data-testid={`${testPrefix}-sport-${i}`} />
                    Sport dropdown
                  </label>
                  {kids.length > 0 && (
                    <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer"
                      title="On phones this stays a plain link instead of opening the dropdown">
                      <input type="checkbox" checked={!!it.mobile_flat}
                        onChange={e => patchAt(i, { mobile_flat: e.target.checked })}
                        data-testid={`${testPrefix}-flat-${i}`} />
                      Flat on mobile
                    </label>
                  )}
                  <Button size="sm" variant="ghost"
                    className="h-5 px-1.5 text-[10px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
                    onClick={() => patchAt(i, { children: [...kids, { label: '', slug: '' }] })}
                    data-testid={`${testPrefix}-addchild-${i}`}>
                    <Plus className="h-2.5 w-2.5 mr-0.5" />Sub-item
                  </Button>
                </>
              )}
            </div>

            {depth === 0 && kids.length > 0 && (
              <div className="mt-2 ml-6 pl-2 border-l border-slate-700/60">
                <NavItemRows items={kids} depth={1}
                  testPrefix={`${testPrefix}-${i}-child`}
                  onChange={(next) => patchAt(i, { children: next })} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Header Navigation sheet ────────────────────────────────────── */
function NavSheetBody({ nav, setN }) {
  const primary = Array.isArray(nav.primary) ? nav.primary : [];
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Header Menu</p>
          <Button size="sm" variant="ghost"
            className="h-6 text-[11px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            onClick={() => setN('primary', [...primary, { label: '', slug: '' }])}
            data-testid="nav-primary-add">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        {primary.length === 0 ? (
          <p className="text-[11px] text-slate-500 leading-relaxed">
            No menu configured — the website keeps rendering its built-in header.
            Add items here to take control of it.
          </p>
        ) : (
          <NavItemRows items={primary} testPrefix="nav-primary"
            onChange={(next) => setN('primary', next)} />
        )}
        <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
          Slugs are site paths without the domain — <code className="bg-slate-700/80 px-1 rounded text-amber-300">members/app</code>{' '}
          becomes <code className="bg-slate-700/80 px-1 rounded text-amber-300">/members/app/</code>.
          Sign In / Sign Out is added by the site automatically. Clearing the list
          restores the built-in menu.
        </p>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Logo Media-Library Tag</Label>
        <Input value={nav.logo_media_tag}
          onChange={e => setN('logo_media_tag', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          className="bg-slate-900 border-slate-600 font-mono h-8 text-sm mt-1 text-white"
          data-testid="nav-logo-tag" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          WP uses the most-recent attachment whose slug starts with this prefix.
          Default <code className="bg-slate-700/80 px-1 rounded text-amber-300">nav-logo</code>.
        </p>
      </div>
      <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-1">Update the Logo</p>
        <ol className="text-[11px] text-slate-400 space-y-1 list-decimal pl-4 leading-relaxed">
          <li>Upload the new logo to the WordPress Media Library.</li>
          <li>Set its slug to start with <code className="bg-slate-700/80 px-1 rounded text-amber-300">{nav.logo_media_tag}</code>{' '}
              (e.g. <code className="bg-slate-700/80 px-1 rounded text-amber-300">{nav.logo_media_tag}-2026</code>).</li>
          <li>WP reads it within the hour — or click <strong className="text-slate-200">Force Refresh</strong> above to apply immediately.</li>
        </ol>
      </div>
    </div>
  );
}

/* ── Popup Notifications sheet content (dense list configurator) ─────────── */
function PopupNotificationsSheetBody({ popup, setPopup, sports }) {
  const items = Array.isArray(popup?.items) ? popup.items : [];
  const [openId, setOpenId] = useState(null);   // inline-expanded row (one at a time)
  const [dragId, setDragId] = useState(null);

  const commit = (next) => setPopup({ items: next.map((it, i) => ({ ...it, order: i })) });
  const patchAt = (idx, patch) => { const next = [...items]; next[idx] = { ...next[idx], ...patch }; commit(next); };
  const removeAt = (idx) => { const removed = items[idx]; const next = [...items]; next.splice(idx, 1); commit(next); if (openId === removed?.id) setOpenId(null); };
  const add = () => { const it = newPopupItem(); commit([...items, it]); setOpenId(it.id); };

  const onDrop = (targetIdx) => {
    if (dragId == null) { return; }
    const from = items.findIndex(i => i.id === dragId);
    setDragId(null);
    if (from < 0 || from === targetIdx) { return; }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    commit(next);
  };

  const shortWindow = (it) => {
    const f = (s) => { if (!s) { return ''; } const d = new Date(s); return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`; };
    const a = f(it.starts_at); const b = f(it.ends_at);
    if (!a && !b) { return 'always'; }
    return `${a || '…'}–${b || '…'}`;
  };

  return (
    <div className="space-y-3" data-testid="popup-notifications-sheet-body">
      <p className="text-[11px] text-slate-400">
        Each enabled notice inside its date window shows as a rotating bulletin on the right of the sponsor bar.
        Drag <GripVertical className="inline h-3 w-3" /> to reorder — the first is shown first.
      </p>

      <div className="rounded-md border border-slate-700 divide-y divide-slate-800" data-testid="popup-notifications-list">
        {items.length === 0 && (
          <p className="text-[11px] text-slate-500 px-3 py-4 text-center" data-testid="popup-empty">No notices yet. Add one below.</p>
        )}
        {items.map((it, idx) => {
          const expanded = openId === it.id;
          const newTabVal = (it.open_in_new_tab === null || it.open_in_new_tab === undefined) ? 'auto' : (it.open_in_new_tab ? 'yes' : 'no');
          return (
            <div key={it.id}
              draggable
              onDragStart={() => setDragId(it.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className={dragId === it.id ? 'opacity-50' : ''}
              data-testid={`popup-row-${idx}`}>
              {/* compact row (~36px) */}
              <div className="flex items-center gap-2 px-2 h-9">
                <GripVertical className="h-3.5 w-3.5 text-slate-500 cursor-grab flex-shrink-0" data-testid={`popup-drag-${idx}`} />
                <Toggle checked={!!it.enabled} onChange={v => patchAt(idx, { enabled: v })} testId={`popup-enabled-${idx}`} />
                <span className="flex-1 min-w-0 truncate text-xs text-white" title={it.label} data-testid={`popup-label-text-${idx}`}>
                  {it.label || <span className="text-slate-500 italic">Untitled notice</span>}
                </span>
                <span className="hidden sm:inline text-[10px] text-slate-400 capitalize w-16 truncate">{it.sport || 'all'}</span>
                <span className="h-4 w-4 rounded-sm border border-slate-600 flex-shrink-0" style={{ background: it.button_color || DEFAULT_POPUP_COLOR }} data-testid={`popup-swatch-${idx}`} />
                <span className="text-[10px] text-slate-400 w-20 text-right tabular-nums truncate" data-testid={`popup-window-${idx}`}>{shortWindow(it)}</span>
                <button type="button" className="p-1 text-slate-400 hover:text-white" onClick={() => setOpenId(expanded ? null : it.id)} data-testid={`popup-edit-${idx}`}>
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <Pencil className="h-3.5 w-3.5" />}
                </button>
                <button type="button" className="p-1 text-slate-400 hover:text-red-400" onClick={() => removeAt(idx)} data-testid={`popup-delete-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* inline expand (URL, new-tab, exact dates, colour) */}
              {expanded && (
                <div className="px-3 pb-3 pt-1 bg-slate-900/50 space-y-3" data-testid={`popup-expand-${idx}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-300">Label</Label>
                      <Input value={it.label || ''} onChange={e => patchAt(idx, { label: e.target.value })}
                        className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" placeholder="Basketball Registration" data-testid={`popup-label-input-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-300">Sport (optional)</Label>
                      <select value={it.sport || ''} onChange={e => patchAt(idx, { sport: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-600 rounded-md h-8 text-sm mt-1 text-white px-2 capitalize" data-testid={`popup-sport-${idx}`}>
                        <option value="">All / none</option>
                        {(sports || []).map(s => (<option key={s.value} value={s.value} className="capitalize">{s.label}</option>))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Link URL</Label>
                    <Input value={it.url || ''} onChange={e => patchAt(idx, { url: e.target.value })}
                      className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white font-mono" placeholder="https://example.com/register/form/..." data-testid={`popup-url-${idx}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-300">Starts at</Label>
                      <Input type="datetime-local" value={(it.starts_at || '').slice(0, 16)} onChange={e => patchAt(idx, { starts_at: e.target.value })}
                        className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid={`popup-starts-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-300">Ends at</Label>
                      <Input type="datetime-local" value={(it.ends_at || '').slice(0, 16)} onChange={e => patchAt(idx, { ends_at: e.target.value })}
                        className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid={`popup-ends-${idx}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-300">Button colour</Label>
                      <input type="color" value={it.button_color || DEFAULT_POPUP_COLOR} onChange={e => patchAt(idx, { button_color: e.target.value })}
                        className="h-7 w-10 bg-transparent border border-slate-600 rounded cursor-pointer" data-testid={`popup-color-${idx}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-300">Open in new tab</Label>
                      <select value={newTabVal}
                        onChange={e => { const v = e.target.value; patchAt(idx, { open_in_new_tab: v === 'auto' ? null : v === 'yes' }); }}
                        className="bg-slate-900 border border-slate-600 rounded-md h-8 text-xs text-white px-2" data-testid={`popup-newtab-${idx}`}>
                        <option value="auto">Auto (off-site → new tab)</option>
                        <option value="yes">Always new tab</option>
                        <option value="no">Same tab</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500">Leave both dates blank for always-on while enabled.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button size="sm" variant="outline" onClick={add}
        className="w-full h-8 text-[11px] border-slate-600 text-slate-300" data-testid="popup-add-btn">
        <Plus className="h-3 w-3 mr-1" /> Add notice
      </Button>
    </div>
  );
}



/* ── Footer sheet content ───────────────────────────────────────────────── */
function FooterSheetBody({ footer, setF, setFooter }) {
  // Generic helpers for the array-of-objects fields.
  const updateAt = (key, idx, patch) => {
    const next = [...(footer[key] || [])];
    next[idx] = { ...next[idx], ...patch };
    setF(key, next);
  };
  const addItem = (key, item) => setF(key, [...(footer[key] || []), item]);
  const removeAt = (key, idx) => {
    const next = [...(footer[key] || [])];
    next.splice(idx, 1);
    setF(key, next);
  };
  const setNested = (parent, child, val) =>
    setFooter(prev => ({ ...prev, [parent]: { ...(prev[parent] || {}), [child]: val } }));

  return (
    <div className="space-y-7" data-testid="footer-sheet-body">
      {/* ── Tagline ────────────────────────────────────────────────────── */}
      <div>
        <Label className="text-xs text-slate-300">Site Tagline</Label>
        <Input value={footer.tagline}
          onChange={e => setF('tagline', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          placeholder="Building Champions, On and Off the Court."
          data-testid="footer-tagline" />
      </div>

      {/* ── Address block ─────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-2">Address</p>
        <div className="grid grid-cols-1 gap-2">
          <Input value={footer.address?.line1 || ''}
            onChange={e => setNested('address', 'line1', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
            placeholder="Street address"
            data-testid="footer-address-line1" />
          <Input value={footer.address?.line2 || ''}
            onChange={e => setNested('address', 'line2', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
            placeholder="Suite / unit (optional)"
            data-testid="footer-address-line2" />
          <div className="grid grid-cols-3 gap-2">
            <Input value={footer.address?.city || ''}
              onChange={e => setNested('address', 'city', e.target.value)}
              className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
              placeholder="City"
              data-testid="footer-address-city" />
            <Input value={footer.address?.state || ''}
              onChange={e => setNested('address', 'state', e.target.value.toUpperCase())}
              className="bg-slate-900 border-slate-600 h-8 text-sm uppercase text-white"
              placeholder="ST"
              maxLength={2}
              data-testid="footer-address-state" />
            <Input value={footer.address?.zip || ''}
              onChange={e => setNested('address', 'zip', e.target.value)}
              className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
              placeholder="ZIP"
              data-testid="footer-address-zip" />
          </div>
        </div>
      </div>

      {/* ── Contact ───────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-2">Contact</p>
        <div className="grid grid-cols-2 gap-2">
          <Input type="email" value={footer.contact?.email || ''}
            onChange={e => setNested('contact', 'email', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
            placeholder="info@org.com"
            data-testid="footer-contact-email" />
          <Input value={footer.contact?.phone || ''}
            onChange={e => setNested('contact', 'phone', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm text-white"
            placeholder="(555) 123-4567"
            data-testid="footer-contact-phone" />
        </div>
      </div>

      {/* ── Social links ─────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Social Links</p>
          <Button size="sm" variant="ghost"
            className="h-6 text-[11px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            onClick={() => addItem('social_links', { platform: 'facebook', url: '' })}
            data-testid="footer-social-add">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        {(footer.social_links || []).length === 0 && (
          <p className="text-[11px] text-slate-500 italic">No social links configured.</p>
        )}
        <div className="space-y-2">
          {(footer.social_links || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`footer-social-row-${i}`}>
              <select value={s.platform || 'facebook'}
                onChange={e => updateAt('social_links', i, { platform: e.target.value })}
                className="bg-slate-900 border border-slate-600 h-8 text-xs text-white rounded px-2"
                data-testid={`footer-social-platform-${i}`}>
                {SOCIAL_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <Input value={s.url || ''}
                onChange={e => updateAt('social_links', i, { url: e.target.value })}
                className="bg-slate-900 border-slate-600 h-8 text-xs text-white flex-1"
                placeholder="https://..."
                data-testid={`footer-social-url-${i}`} />
              <Button size="sm" variant="ghost"
                className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                onClick={() => removeAt('social_links', i)}
                data-testid={`footer-social-del-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Quick links ──────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Quick Links</p>
          <Button size="sm" variant="ghost"
            className="h-6 text-[11px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            onClick={() => addItem('quick_links', { label: '', slug: '' })}
            data-testid="footer-ql-add">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        <div className="space-y-2">
          {(footer.quick_links || []).map((q, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`footer-ql-row-${i}`}>
              <Input value={q.label || ''}
                onChange={e => updateAt('quick_links', i, { label: e.target.value })}
                className="bg-slate-900 border-slate-600 h-8 text-xs text-white flex-1"
                placeholder="Label (e.g. Register)"
                data-testid={`footer-ql-label-${i}`} />
              <Input value={q.slug || ''}
                onChange={e => updateAt('quick_links', i, { slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="bg-slate-900 border-slate-600 h-8 text-xs font-mono text-white flex-1"
                placeholder="slug"
                data-testid={`footer-ql-slug-${i}`} />
              <Button size="sm" variant="ghost"
                className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                onClick={() => removeAt('quick_links', i)}
                data-testid={`footer-ql-del-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Sport links ──────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-2">Sport Links</p>
        <div className="flex gap-3 mb-2">
          {[
            { v: 'auto',   l: 'Auto (from Active Sports)' },
            { v: 'custom', l: 'Custom list' },
          ].map(o => (
            <label key={o.v} className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-300">
              <input type="radio" name="sport-links-mode"
                checked={footer.sport_links_mode === o.v}
                onChange={() => setF('sport_links_mode', o.v)}
                data-testid={`footer-sport-mode-${o.v}`} />
              {o.l}
            </label>
          ))}
        </div>
        {footer.sport_links_mode === 'auto' && (
          <p className="text-[11px] text-slate-500 italic">
            WP derives one link per sport in <code className="bg-slate-700/80 px-1 rounded text-amber-300">upcoming_games.active_sports</code>,
            linking to <code className="bg-slate-700/80 px-1 rounded text-amber-300">/sports/&#123;slug&#125;</code>.
          </p>
        )}
        {footer.sport_links_mode === 'custom' && (
          <>
            <div className="flex justify-end mb-2">
              <Button size="sm" variant="ghost"
                className="h-6 text-[11px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
                onClick={() => addItem('sport_links_custom', { sport: '', label: '', slug: '' })}
                data-testid="footer-sportlink-add">
                <Plus className="h-3 w-3 mr-1" />Add
              </Button>
            </div>
            <div className="space-y-2">
              {(footer.sport_links_custom || []).map((sl, i) => (
                <div key={i} className="grid grid-cols-3 gap-2" data-testid={`footer-sportlink-row-${i}`}>
                  <Input value={sl.sport || ''}
                    onChange={e => updateAt('sport_links_custom', i, { sport: e.target.value.toLowerCase() })}
                    className="bg-slate-900 border-slate-600 h-8 text-xs text-white"
                    placeholder="basketball"
                    data-testid={`footer-sportlink-sport-${i}`} />
                  <Input value={sl.label || ''}
                    onChange={e => updateAt('sport_links_custom', i, { label: e.target.value })}
                    className="bg-slate-900 border-slate-600 h-8 text-xs text-white"
                    placeholder="Boys Basketball"
                    data-testid={`footer-sportlink-label-${i}`} />
                  <div className="flex gap-1">
                    <Input value={sl.slug || ''}
                      onChange={e => updateAt('sport_links_custom', i, { slug: e.target.value })}
                      className="bg-slate-900 border-slate-600 h-8 text-xs font-mono text-white flex-1"
                      placeholder="sports/basketball"
                      data-testid={`footer-sportlink-slug-${i}`} />
                    <Button size="sm" variant="ghost"
                      className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                      onClick={() => removeAt('sport_links_custom', i)}
                      data-testid={`footer-sportlink-del-${i}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Sponsor strip toggle ─────────────────────────────────────── */}
      <div className="flex items-center justify-between p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <div>
          <Label className="text-xs text-slate-200">Show Sponsor Strip</Label>
          <p className="text-[10px] text-slate-500 mt-0.5">Mirrors the home-page sponsor bar in the footer.</p>
        </div>
        <Toggle
          checked={!!footer.sponsor_strip_enabled}
          onChange={v => setF('sponsor_strip_enabled', v)}
          testId="footer-sponsor-strip-toggle"
        />
      </div>

      {/* ── Legal links ──────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Legal Links</p>
          <Button size="sm" variant="ghost"
            className="h-6 text-[11px] text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
            onClick={() => addItem('legal_links', { label: '', slug: '' })}
            data-testid="footer-legal-add">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        <div className="space-y-2">
          {(footer.legal_links || []).map((l, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`footer-legal-row-${i}`}>
              <Input value={l.label || ''}
                onChange={e => updateAt('legal_links', i, { label: e.target.value })}
                className="bg-slate-900 border-slate-600 h-8 text-xs text-white flex-1"
                placeholder="Label"
                data-testid={`footer-legal-label-${i}`} />
              <Input value={l.slug || ''}
                onChange={e => updateAt('legal_links', i, { slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="bg-slate-900 border-slate-600 h-8 text-xs font-mono text-white flex-1"
                placeholder="slug"
                data-testid={`footer-legal-slug-${i}`} />
              <Button size="sm" variant="ghost"
                className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                onClick={() => removeAt('legal_links', i)}
                data-testid={`footer-legal-del-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Copyright + powered-by ───────────────────────────────────── */}
      <div>
        <Label className="text-xs text-slate-300">Copyright Text</Label>
        <Input value={footer.copyright_text}
          onChange={e => setF('copyright_text', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white"
          data-testid="footer-copyright" />
        <p className="text-[10px] text-slate-500 mt-0.5">
          Tokens: <code className="bg-slate-700/80 px-1 rounded text-amber-300">{'{YEAR}'}</code>,
          {' '}<code className="bg-slate-700/80 px-1 rounded text-amber-300">{'{ORG_NAME}'}</code> are filled by WP at render.
        </p>
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <div className="flex-1 mr-3">
          <Label className="text-xs text-slate-200">"Powered by" Credit</Label>
          <Input value={footer.powered_by_text}
            onChange={e => setF('powered_by_text', e.target.value)}
            className="bg-slate-900 border-slate-600 h-7 text-xs mt-1 text-white"
            disabled={!footer.show_powered_by}
            data-testid="footer-powered-by-text" />
        </div>
        <Toggle
          checked={!!footer.show_powered_by}
          onChange={v => setF('show_powered_by', v)}
          testId="footer-powered-by-toggle"
        />
      </div>

      {/* ── Colours ──────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-2">Footer Colours</p>
        <div className="grid grid-cols-1 gap-3">
          <ColorField label="Background"   value={footer.bg_color}
            onChange={v => setF('bg_color', v)} testId="footer-bg-color" />
          <ColorField label="Body Text"    value={footer.text_color}
            onChange={v => setF('text_color', v)} testId="footer-text-color" />
          <ColorField label="Accent (links / icons)" value={footer.accent_color}
            onChange={v => setF('accent_color', v)} testId="footer-accent-color" />
        </div>
      </div>
    </div>
  );
}

/* ── ORG ID RESOLUTION ──────────────────────────────────────────────────
   Multi-tenant (Phase 5, Session 16, May 11 2026; finalised Session 24,
   May 12 2026): the component accepts an explicit `orgId` prop from
   TechSettingsPanel (the active org). The hardcoded UUID is retained
   ONLY as a defensive safety fallback for any caller that forgets to
   pass `orgId`, with a console.warn so we notice if it ever triggers
   in production. The matching WP plugin reads its own UUID from
   `wp_options.ol_org_id` (also with a fallback constant) — see
   /app/docs/WP_PLUGIN_MULTI_TENANT_BRIEF.md. */
const PINNED_ORG_ID_FALLBACK = '7de728e4-76ad-4254-b031-7b7b7eea9436';

/* ── Main component ─────────────────────────────────────────────────────── */
/* ── Sponsorship Packages sheet (OL SOT → /community/sponsorship/) ───────── */
function SponsorPackagesSheetBody({ val, setVal }) {
  const up = (k, v) => setVal({ ...val, [k]: v });
  const pkgs = val.packages || [];
  const setPkg = (i, f, v) => { const n = [...pkgs]; n[i] = { ...n[i], [f]: v }; up('packages', n); };
  const setBenefit = (pi, bi, v) => { const n = [...pkgs]; const b = [...(n[pi].benefits || [])]; b[bi] = v; n[pi] = { ...n[pi], benefits: b }; up('packages', n); };
  const addBenefit = (pi) => { const n = [...pkgs]; n[pi] = { ...n[pi], benefits: [...(n[pi].benefits || []), ''] }; up('packages', n); };
  const removeBenefit = (pi, bi) => { const n = [...pkgs]; const b = [...(n[pi].benefits || [])]; b.splice(bi, 1); n[pi] = { ...n[pi], benefits: b }; up('packages', n); };
  const addPkg = () => up('packages', [...pkgs, { name: 'New Level', price: '$0', benefits: [] }]);
  const removePkg = (i) => { const n = [...pkgs]; n.splice(i, 1); up('packages', n); };
  const ta = 'w-full bg-slate-900 border border-slate-600 rounded-md text-sm text-white p-2';

  return (
    <div className="space-y-6" data-testid="sponsor-packages-body">
      <div>
        <Label className="text-xs text-slate-300">Section Heading</Label>
        <Input value={val.heading || ''} onChange={e => up('heading', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="pkg-heading" />
      </div>
      <div>
        <Label className="text-xs text-slate-300">Intro Paragraph</Label>
        <textarea value={val.intro || ''} onChange={e => up('intro', e.target.value)} rows={3}
          className={`${ta} mt-1`} data-testid="pkg-intro" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Levels</p>
          <Button size="sm" onClick={addPkg} className="bg-amber-600 hover:bg-amber-700 h-7" data-testid="pkg-add">
            <Plus className="h-3.5 w-3.5 mr-1" />Add Level
          </Button>
        </div>
        <div className="space-y-4">
          {pkgs.map((p, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-700 bg-slate-900/50 space-y-2" data-testid={`pkg-${i}`}>
              <div className="flex gap-2">
                <Input value={p.name || ''} onChange={e => setPkg(i, 'name', e.target.value)} placeholder="Level name"
                  className="bg-slate-900 border-slate-600 h-8 text-sm text-white flex-1" data-testid={`pkg-${i}-name`} />
                <Input value={p.price || ''} onChange={e => setPkg(i, 'price', e.target.value)} placeholder="$"
                  className="bg-slate-900 border-slate-600 h-8 text-sm text-white w-24" data-testid={`pkg-${i}-price`} />
                <button type="button" onClick={() => removePkg(i)} className="p-1.5 rounded hover:bg-red-500/20" data-testid={`pkg-${i}-remove`}>
                  <X className="h-4 w-4 text-red-300" />
                </button>
              </div>
              <div className="space-y-1.5">
                {(p.benefits || []).map((b, bi) => (
                  <div key={bi} className="flex gap-2 items-center">
                    <Input value={b} onChange={e => setBenefit(i, bi, e.target.value)} placeholder="Benefit"
                      className="bg-slate-900 border-slate-600 h-7 text-xs text-white flex-1" data-testid={`pkg-${i}-benefit-${bi}`} />
                    <button type="button" onClick={() => removeBenefit(i, bi)} className="p-1 rounded hover:bg-red-500/20">
                      <X className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => addBenefit(i)}
                  className="h-6 text-[10px] border-slate-600 text-slate-300" data-testid={`pkg-${i}-add-benefit`}>
                  <Plus className="h-3 w-3 mr-1" />Benefit
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Footnote</Label>
        <textarea value={val.footnote || ''} onChange={e => up('footnote', e.target.value)} rows={3}
          className={`${ta} mt-1`} data-testid="pkg-footnote" />
      </div>
      <div>
        <Label className="text-xs text-slate-300">Tax Note</Label>
        <Input value={val.tax_note || ''} onChange={e => up('tax_note', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="pkg-tax" />
      </div>
      <div>
        <Label className="text-xs text-slate-300">CTA Link (optional — overrides email)</Label>
        <Input value={val.cta_url || ''} onChange={e => up('cta_url', e.target.value)} placeholder="https://… (leave blank to email)"
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="pkg-cta-url" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-300">CTA Email</Label>
          <Input value={val.cta_email || ''} onChange={e => up('cta_email', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="pkg-cta-email" />
        </div>
        <div>
          <Label className="text-xs text-slate-300">CTA Button Label</Label>
          <Input value={val.cta_label || ''} onChange={e => up('cta_label', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="pkg-cta-label" />
        </div>
      </div>
    </div>
  );
}

function DonationSheetBody({ val, setVal, orgId }) {
  const up = (k, v) => setVal({ ...val, [k]: v });
  const qrSrc = val.donate_url
    ? `${API}/api/website-settings/${orgId}/donation-qr.webp?data=${encodeURIComponent(val.donate_url)}`
    : '';
  return (
    <div className="space-y-5" data-testid="donation-body">
      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" checked={!!val.enabled} onChange={e => up('enabled', e.target.checked)} data-testid="donation-enabled" />
        Show the donation button + QR on the Sponsors &amp; Packages pages
      </label>
      <div>
        <Label className="text-xs text-slate-300">Donation Link (URL)</Label>
        <Input value={val.donate_url || ''} onChange={e => up('donate_url', e.target.value)} placeholder="https://…"
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="donation-url" />
        <p className="text-[11px] text-slate-500 mt-1">Paste the link donors should reach (PayPal, Givebutter, Zeffy, etc.). The QR code is generated automatically from this link.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-300">Heading</Label>
          <Input value={val.heading || ''} onChange={e => up('heading', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="donation-heading" />
        </div>
        <div>
          <Label className="text-xs text-slate-300">Button Label</Label>
          <Input value={val.button_label || ''} onChange={e => up('button_label', e.target.value)}
            className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="donation-btn-label" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Note (shown under the QR)</Label>
        <Input value={val.note || ''} onChange={e => up('note', e.target.value)}
          className="bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white" data-testid="donation-note" />
      </div>
      <div className="pt-1">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">QR Code Preview</p>
        {qrSrc ? (
          <img src={qrSrc} alt="Donation QR preview" width={180} height={180}
            className="rounded-lg bg-white p-2" data-testid="donation-qr-preview" />
        ) : (
          <p className="text-xs text-slate-500">Enter a donation link above to generate the QR code.</p>
        )}
      </div>
    </div>
  );
}

function ExploreMoreSheetBody({ val, setVal }) {
  const cards = Array.isArray(val.cards) ? val.cards : [];
  const inCls = 'bg-slate-900 border-slate-600 h-8 text-sm mt-1 text-white';
  const updCard = (i, field, v) => setVal({ ...val, cards: cards.map((c, idx) => (idx === i ? { ...c, [field]: v } : c)) });
  const addCard = () => setVal({ ...val, cards: [...cards, { icon: '', title: '', body: '', link_label: '', link_url: '', action: '' }] });
  const removeCard = (i) => setVal({ ...val, cards: cards.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs text-slate-300">Show this section</Label>
          <p className="text-[11px] text-slate-500">Toggle the whole Explore More block on the homepage.</p>
        </div>
        <Toggle checked={val.enabled !== false} onChange={v => setVal({ ...val, enabled: v })} testId="explore-enabled-toggle" />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label className="text-xs text-slate-300">Section Title</Label>
          <Input value={val.title || ''} onChange={e => setVal({ ...val, title: e.target.value })} className={inCls} data-testid="explore-title" />
        </div>
        <div>
          <Label className="text-xs text-slate-300">Subtitle</Label>
          <Input value={val.subtitle || ''} onChange={e => setVal({ ...val, subtitle: e.target.value })} className={inCls} data-testid="explore-subtitle" />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Cards</p>
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={addCard} data-testid="explore-card-add"><Plus className="h-3 w-3 mr-1" />Add Card</Button>
        </div>
        <div className="space-y-3" data-testid="explore-cards-list">
          {cards.map((c, i) => (
            <div key={i} className="border border-slate-700 rounded-lg p-3 space-y-2 bg-slate-800/40" data-testid={`explore-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Card {i + 1}</span>
                <button onClick={() => removeCard(i)} className="text-slate-400 hover:text-red-400" data-testid={`explore-card-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1"><Label className="text-xs text-slate-300">Icon</Label><Input value={c.icon || ''} onChange={e => updCard(i, 'icon', e.target.value)} className={inCls} placeholder="📅" data-testid={`explore-card-icon-${i}`} /></div>
                <div className="col-span-3"><Label className="text-xs text-slate-300">Title</Label><Input value={c.title || ''} onChange={e => updCard(i, 'title', e.target.value)} className={inCls} data-testid={`explore-card-title-${i}`} /></div>
              </div>
              <div><Label className="text-xs text-slate-300">Body</Label><Input value={c.body || ''} onChange={e => updCard(i, 'body', e.target.value)} className={inCls} data-testid={`explore-card-body-${i}`} /></div>
              <div><Label className="text-xs text-slate-300">Link Label</Label><Input value={c.link_label || ''} onChange={e => updCard(i, 'link_label', e.target.value)} className={inCls} data-testid={`explore-card-link-label-${i}`} /></div>
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={c.action === 'schedules_modal'} onChange={e => updCard(i, 'action', e.target.checked ? 'schedules_modal' : '')} data-testid={`explore-card-modal-${i}`} />
                Open the Schedules pop-up (instead of a link)
              </label>
              {c.action !== 'schedules_modal' && (
                <div><Label className="text-xs text-slate-300">Link URL</Label><Input value={c.link_url || ''} onChange={e => updCard(i, 'link_url', e.target.value)} className={inCls} placeholder="/teams/" data-testid={`explore-card-link-url-${i}`} /></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WebsiteSettingsSection({ orgId } = {}) {
  if (!orgId) {
    // eslint-disable-next-line no-console
    console.warn('[WebsiteSettingsSection] orgId prop missing — falling back to pinned SWS UUID. ' +
      'Pass orgId explicitly from the parent for true multi-tenant behaviour.');
  }
  const effectiveOrgId = orgId || PINNED_ORG_ID_FALLBACK;

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings]     = useState(null);
  const [meta, setMeta]             = useState({ version: 0, updated_at: null });

  /* Local edit states per section — extended as new sections are built */
  const [localTicker, setLocalTicker]     = useState({ ...DEFAULT_TICKER });
  const [localSponsors, setLocalSponsors] = useState({ ...DEFAULT_SPONSORS });
  const [localSponsorPackages, setLocalSponsorPackages] = useState({ ...DEFAULT_SPONSOR_PACKAGES });
  const [localDonation, setLocalDonation] = useState({ ...DEFAULT_DONATION });
  const [localHero, setLocalHero]         = useState({ ...DEFAULT_HERO });
  const [localUg, setLocalUg]             = useState({ ...DEFAULT_UPCOMING_GAMES });
  const [localHs, setLocalHs]             = useState({ ...DEFAULT_HOME_SECTIONS });
  const [localExplore, setLocalExplore]   = useState({ ...DEFAULT_EXPLORE_MORE });
  const [localStats, setLocalStats]       = useState([...DEFAULT_STATS]);
  const [localStatsOverrides, setLocalStatsOverrides] = useState({}); // { sport: { program_start_year, team_count } }
  const [localTv, setLocalTv]             = useState({ ...DEFAULT_SABRES_TV });
  const [localJc, setLocalJc]             = useState({ ...DEFAULT_JOIN_CTA });
  const [localNav, setLocalNav]           = useState({ ...DEFAULT_NAV });
  const [localFooter, setLocalFooter]     = useState({ ...DEFAULT_FOOTER });
  const [localRegisterCta, setLocalRegisterCta] = useState({ ...DEFAULT_REGISTER_CTA });
  const [localPopup, setLocalPopup] = useState({ ...DEFAULT_POPUP_NOTIFICATIONS });
  const [orgSports, setOrgSports] = useState([]); // [{value,label}] from the org sports SOT
  const [localLeadership, setLocalLeadership] = useState({ ...DEFAULT_LEADERSHIP });
  const [localAboutPage, setLocalAboutPage] = useState({ ...DEFAULT_ABOUT_PAGE });
  const [localMembersPage, setLocalMembersPage] = useState({ ...DEFAULT_MEMBERS_PAGE });
  const [localStandings, setLocalStandings] = useState({ ...DEFAULT_STANDINGS });
  const [localPermissions, setLocalPermissions] = useState({ ...DEFAULT_PERMISSIONS });

  const [activeSection, setActiveSection] = useState(null); // key of open sheet

  // Always reset local edit state from confirmed DB values when opening a sheet
  const openSection = (key) => {
    if (key === 'ticker')         setLocalTicker  ({ ...DEFAULT_TICKER,         ...(settings?.ticker         || {}) });
    if (key === 'sponsors')       setLocalSponsors({ ...DEFAULT_SPONSORS,       ...(settings?.sponsors       || {}) });
    if (key === 'sponsor_packages') setLocalSponsorPackages({ ...DEFAULT_SPONSOR_PACKAGES, ...(settings?.sponsor_packages || {}) });
    if (key === 'donation')         setLocalDonation({ ...DEFAULT_DONATION, ...(settings?.donation || {}) });
    if (key === 'hero')           setLocalHero    ({ ...DEFAULT_HERO,           ...(settings?.hero           || {}) });
    if (key === 'upcoming_games') setLocalUg      ({ ...DEFAULT_UPCOMING_GAMES, ...(settings?.upcoming_games || {}) });
    if (key === 'home_sections')  setLocalHs      ({ ...DEFAULT_HOME_SECTIONS,  ...(settings?.home_sections  || {}) });
    if (key === 'explore_more')   setLocalExplore ({ ...DEFAULT_EXPLORE_MORE,   ...(settings?.explore_more   || {}) });
    if (key === 'stats') {
      setLocalStats(Array.isArray(settings?.stats) ? [...settings.stats] : []);
      setLocalStatsOverrides(settings?.stats_overrides || {});
    }
    if (key === 'sabres_tv')      setLocalTv      ({ ...DEFAULT_SABRES_TV,      ...(settings?.sabres_tv      || {}) });
    if (key === 'join_cta')       setLocalJc      ({ ...DEFAULT_JOIN_CTA,       ...(settings?.join_cta       || {}) });
    if (key === 'nav')            setLocalNav    ({ ...DEFAULT_NAV,            ...(settings?.nav            || {}) });
    if (key === 'footer')         setLocalFooter ({ ...DEFAULT_FOOTER,         ...(settings?.footer         || {}) });
    if (key === 'register_cta')   setLocalRegisterCta({ ...DEFAULT_REGISTER_CTA, ...(settings?.register_cta   || {}) });
    if (key === 'popup_notifications') setLocalPopup({ items: [ ...((settings?.popup_notifications?.items) || []) ] });
    if (key === 'leadership')     setLocalLeadership ({ ...DEFAULT_LEADERSHIP,     ...(settings?.leadership     || {}) });
    if (key === 'about_page')     setLocalAboutPage  ({ ...DEFAULT_ABOUT_PAGE,     ...(settings?.about_page     || {}) });
    if (key === 'members_page')   setLocalMembersPage({ ...DEFAULT_MEMBERS_PAGE,   ...(settings?.members_page   || {}) });
    if (key === 'standings')      setLocalStandings  ({ ...DEFAULT_STANDINGS,      ...(settings?.standings      || {}) });
    if (key === 'permissions')    setLocalPermissions({ ...DEFAULT_PERMISSIONS,    ...(settings?.permissions    || {}) });
    setActiveSection(key);
  };
  const [msg, setMsg]   = useState(null);
  const [copied, setCopied] = useState(false);

  // OL API base URL that the connected WP site fetches from (ol_base_url).
  // TS-editable so a PROD OL cutover needs no SSH.
  const [wpBaseUrl, setWpBaseUrl] = useState('');
  const [wpBaseDraft, setWpBaseDraft] = useState('');
  const [editingBase, setEditingBase] = useState(false);
  const [savingBase, setSavingBase] = useState(false);
  const baseUrlApi = `${API}/api/bridge-proxy/wp-base-url/${effectiveOrgId}`;
  useEffect(() => {
    axios.get(baseUrlApi)
      .then(({ data }) => { if (data?.ok) { setWpBaseUrl(data.ol_base_url || ''); setWpBaseDraft(data.ol_base_url || ''); } })
      .catch(() => {});
  }, [baseUrlApi]);
  const saveBaseUrl = async () => {
    setSavingBase(true);
    try {
      const { data } = await axios.put(baseUrlApi, { ol_base_url: wpBaseDraft.trim() });
      if (data?.ok) {
        setWpBaseUrl(data.ol_base_url);
        setEditingBase(false);
        flashMsg('ok', `WP now fetches OmniLeagues from ${data.ol_base_url}`);
      }
    } catch (e) {
      flashMsg('err', `Couldn't update WP → OL URL: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingBase(false);
    }
  };

  const feedUrl    = `${API}/api/website-settings/${effectiveOrgId}`;
  const versionUrl = `${API}/api/website-settings/${effectiveOrgId}/version`;

  /* ── Load ───────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(feedUrl);
      if (data.ok) {
        setSettings(data);
        setMeta({ version: data.version, updated_at: data.updated_at });
        setLocalTicker  ({ ...DEFAULT_TICKER,         ...(data.ticker         || {}) });
        setLocalSponsors({ ...DEFAULT_SPONSORS,       ...(data.sponsors       || {}) });
        setLocalSponsorPackages({ ...DEFAULT_SPONSOR_PACKAGES, ...(data.sponsor_packages || {}) });
        setLocalDonation({ ...DEFAULT_DONATION, ...(data.donation || {}) });
        setLocalHero    ({ ...DEFAULT_HERO,           ...(data.hero           || {}) });
        setLocalUg      ({ ...DEFAULT_UPCOMING_GAMES, ...(data.upcoming_games || {}) });
        setLocalHs      ({ ...DEFAULT_HOME_SECTIONS,  ...(data.home_sections  || {}) });
        setLocalStats   (Array.isArray(data.stats) ? [...data.stats] : []);
        setLocalStatsOverrides(data.stats_overrides || {});
        setLocalTv      ({ ...DEFAULT_SABRES_TV,      ...(data.sabres_tv      || {}) });
        setLocalJc      ({ ...DEFAULT_JOIN_CTA,       ...(data.join_cta       || {}) });
        setLocalNav     ({ ...DEFAULT_NAV,            ...(data.nav            || {}) });
        setLocalFooter  ({ ...DEFAULT_FOOTER,         ...(data.footer         || {}) });
        setLocalRegisterCta({ ...DEFAULT_REGISTER_CTA, ...(data.register_cta   || {}) });
        setLocalPopup({ items: [ ...((data.popup_notifications?.items) || []) ] });
        setLocalLeadership ({ ...DEFAULT_LEADERSHIP,     ...(data.leadership     || {}) });
        setLocalAboutPage  ({ ...DEFAULT_ABOUT_PAGE,     ...(data.about_page     || {}) });
        setLocalMembersPage({ ...DEFAULT_MEMBERS_PAGE,   ...(data.members_page   || {}) });
        setLocalStandings  ({ ...DEFAULT_STANDINGS,      ...(data.standings      || {}) });
        setLocalPermissions({ ...DEFAULT_PERMISSIONS,    ...(data.permissions    || {}) });
      }
    } catch (e) {
      console.error('WebsiteSettings load:', e);
    } finally {
      setLoading(false);
    }
  }, [feedUrl]);

  useEffect(() => { load(); }, [load]);

  /* Org active sports (SOT) for the Popup Notifications sport dropdown — no hardcoded list. */
  useEffect(() => {
    axios.get(`${API}/api/organizations/${effectiveOrgId}/sports`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.sports || []);
        setOrgSports(list.map(s => ({ value: s.name || s.slug || s.id, label: s.name || s.slug })).filter(o => o.value));
      })
      .catch(() => setOrgSports([]));
  }, [effectiveOrgId]);

  /* ── Helpers ────────────────────────────────────────────────────────── */
  const setT  = (key, val) => setLocalTicker  (prev => ({ ...prev, [key]: val }));
  const setS  = (key, val) => setLocalSponsors(prev => ({ ...prev, [key]: val }));
  const setH  = (key, val) => setLocalHero    (prev => ({ ...prev, [key]: val }));
  const setU  = (key, val) => setLocalUg      (prev => ({ ...prev, [key]: val }));
  const setHS = (key, val) => setLocalHs      (prev => ({ ...prev, [key]: val }));
  const setTv = (key, val) => setLocalTv      (prev => ({ ...prev, [key]: val }));
  const setJ  = (key, val) => setLocalJc      (prev => ({ ...prev, [key]: val }));
  const setN  = (key, val) => setLocalNav     (prev => ({ ...prev, [key]: val }));
  const setF  = (key, val) => setLocalFooter  (prev => ({ ...prev, [key]: val }));
  const setLead = (key, val) => setLocalLeadership(prev => ({ ...prev, [key]: val }));
  const setStd = (key, val) => setLocalStandings(prev => ({ ...prev, [key]: val }));

  const flashMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 5000);
  };

  /* ── Save ───────────────────────────────────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (activeSection === 'ticker')         payload.ticker         = localTicker;
      if (activeSection === 'sponsors')       payload.sponsors       = localSponsors;
      if (activeSection === 'sponsor_packages') payload.sponsor_packages = localSponsorPackages;
      if (activeSection === 'donation')         payload.donation         = localDonation;
      if (activeSection === 'hero')           payload.hero           = localHero;
      if (activeSection === 'upcoming_games') payload.upcoming_games = localUg;
      if (activeSection === 'home_sections')  payload.home_sections  = localHs;
      if (activeSection === 'explore_more')   payload.explore_more   = localExplore;
      if (activeSection === 'stats') {
        payload.stats           = localStats;
        payload.stats_overrides = localStatsOverrides;
      }
      if (activeSection === 'sabres_tv')      payload.sabres_tv      = localTv;
      if (activeSection === 'join_cta')       payload.join_cta       = localJc;
      if (activeSection === 'nav')            payload.nav            = localNav;
      if (activeSection === 'footer')         payload.footer         = localFooter;
      if (activeSection === 'register_cta')   payload.register_cta   = localRegisterCta;
      if (activeSection === 'popup_notifications') payload.popup_notifications = localPopup;
      if (activeSection === 'leadership')     payload.leadership     = localLeadership;
      if (activeSection === 'about_page')     payload.about_page     = localAboutPage;
      if (activeSection === 'members_page')   payload.members_page   = localMembersPage;
      if (activeSection === 'standings')      payload.standings      = localStandings;

      let res;
      try {
        res = await axios.patch(feedUrl, payload);
      } catch (e) {
        // On 401 attempt a silent token refresh then retry once
        if (e.response?.status === 401) {
          await axios.post(`${API}/api/auth/refresh`);
          res = await axios.patch(feedUrl, payload);
        } else {
          throw e;
        }
      }

      const { data } = res;
      if (data.ok) {
        setMeta({ version: data.version, updated_at: data.updated_at });
        setSettings(prev => ({
          ...prev,
          ticker:         data.ticker         ?? prev?.ticker,
          sponsors:       data.sponsors       ?? prev?.sponsors,
          sponsor_packages: data.sponsor_packages ?? prev?.sponsor_packages,
          hero:           data.hero           ?? prev?.hero,
          upcoming_games: data.upcoming_games ?? prev?.upcoming_games,
          home_sections:  data.home_sections  ?? prev?.home_sections,
          stats:          Array.isArray(data.stats) ? data.stats : (prev?.stats ?? []),
          stats_overrides: data.stats_overrides ?? prev?.stats_overrides ?? {},
          sabres_tv:      data.sabres_tv      ?? prev?.sabres_tv,
          join_cta:       data.join_cta       ?? prev?.join_cta,
          nav:            data.nav            ?? prev?.nav,
          footer:         data.footer         ?? prev?.footer,
          register_cta:   data.register_cta   ?? prev?.register_cta,
          popup_notifications: data.popup_notifications ?? prev?.popup_notifications,
          leadership:     data.leadership     ?? prev?.leadership,
          about_page:     data.about_page     ?? prev?.about_page,
          members_page:   data.members_page   ?? prev?.members_page,
          standings:      data.standings      ?? prev?.standings,
          version:        data.version,
          updated_at:     data.updated_at,
        }));
        flashMsg('ok', `Saved — version is now v${data.version}.`);
        setActiveSection(null);
      }
    } catch (e) {
      // Always roll local state back to last confirmed DB state so the UI
      // never shows an unsaved value as if it were saved
      setLocalTicker  ({ ...DEFAULT_TICKER,         ...(settings?.ticker         || {}) });
      setLocalSponsors({ ...DEFAULT_SPONSORS,       ...(settings?.sponsors       || {}) });
      setLocalSponsorPackages({ ...DEFAULT_SPONSOR_PACKAGES, ...(settings?.sponsor_packages || {}) });
      setLocalDonation({ ...DEFAULT_DONATION, ...(settings?.donation || {}) });
      setLocalHero    ({ ...DEFAULT_HERO,           ...(settings?.hero           || {}) });
      setLocalUg      ({ ...DEFAULT_UPCOMING_GAMES, ...(settings?.upcoming_games || {}) });
      setLocalHs      ({ ...DEFAULT_HOME_SECTIONS,  ...(settings?.home_sections  || {}) });
      setLocalStats   (Array.isArray(settings?.stats) ? [...settings.stats] : []);
      setLocalTv      ({ ...DEFAULT_SABRES_TV,      ...(settings?.sabres_tv      || {}) });
      setLocalJc      ({ ...DEFAULT_JOIN_CTA,       ...(settings?.join_cta       || {}) });
      setLocalNav     ({ ...DEFAULT_NAV,            ...(settings?.nav            || {}) });
      setLocalFooter  ({ ...DEFAULT_FOOTER,         ...(settings?.footer         || {}) });
      setLocalRegisterCta({ ...DEFAULT_REGISTER_CTA, ...(settings?.register_cta   || {}) });
      setLocalLeadership ({ ...DEFAULT_LEADERSHIP,     ...(settings?.leadership     || {}) });
      setLocalStandings  ({ ...DEFAULT_STANDINGS,      ...(settings?.standings      || {}) });
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      flashMsg('err',
        status === 401
          ? 'Session expired — reload the page and try saving again.'
          : `Save failed: ${detail}`
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── Force refresh ──────────────────────────────────────────────────── */
  const handleForceRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${feedUrl}/refresh`);
      if (data.ok) {
        setMeta(prev => ({ ...prev, version: data.version }));
        flashMsg('ok', `Cache invalidated — website fetches fresh settings on next page load (v${data.version}).`);
      }
    } catch (e) {
      flashMsg('err', e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(feedUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Active section metadata ────────────────────────────────────────── */
  const activeDef = SECTION_DEFS.find(s => s.key === activeSection);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="website-settings-section">

      {/* ── Pinned-org info banner ─────────────────────────────────────────
          OL UI is hard-pinned to the org_id the WP shortcode polls. */}
      <div className="p-3 bg-emerald-900/15 border border-emerald-700/40 rounded-lg" data-testid="website-pinned-org-bar">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
          <span className="text-[11px] text-emerald-300 uppercase tracking-wide font-semibold">
            Writing to OL Master (Mongo)
          </span>
          <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 bg-emerald-500/10">
            org_id pinned
          </Badge>
        </div>
        <p className="text-xs text-emerald-100/80 font-mono break-all">{effectiveOrgId}</p>
      </div>

      {/* ── Top bar: endpoint URL + global controls ───────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-slate-900/50 border border-slate-700/60 rounded-lg">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="h-3.5 w-3.5 text-teal-400 flex-shrink-0" />
            <span className="text-[11px] text-slate-400 uppercase tracking-wide font-semibold">OL Public Endpoint</span>
            <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 bg-emerald-500/10">
              v{meta.version}
            </Badge>
            {meta.updated_at && (
              <span className="text-[10px] text-slate-500 hidden sm:inline">
                · saved {new Date(meta.updated_at).toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-300 font-mono truncate">{feedUrl}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline"
            className="h-7 text-[11px] border-slate-600 text-slate-300"
            onClick={handleCopy} data-testid="website-copy-url-btn">
            {copied ? <Check className="h-3 w-3 mr-1 text-emerald-400" /> : <Copy className="h-3 w-3 mr-1" />}
            {copied ? 'Copied!' : 'Copy URL'}
          </Button>
          <Button size="sm" variant="outline"
            className="h-7 text-[11px] border-teal-700/60 text-teal-400 hover:bg-teal-900/20"
            onClick={handleForceRefresh} disabled={refreshing}
            data-testid="website-force-refresh-btn">
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Force Refresh
          </Button>
        </div>
      </div>

      {/* ── WP → OL connection URL (TS-editable; needed at PROD cutover) ── */}
      <div className="p-3 bg-indigo-900/15 border border-indigo-700/40 rounded-lg" data-testid="wp-base-url-card">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-3.5 w-3.5 text-indigo-300 flex-shrink-0" />
          <span className="text-[11px] text-indigo-200 uppercase tracking-wide font-semibold">WordPress → OmniLeagues API URL</span>
        </div>
        <p className="text-[11px] text-indigo-100/70 mb-2">
          The OmniLeagues server address the DEV WordPress site pulls all content from. Update this when you move to a new production OL server.
        </p>
        {editingBase ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={wpBaseDraft} onChange={e => setWpBaseDraft(e.target.value)}
              placeholder="https://your-ol-server.com"
              className="bg-slate-900 border-slate-600 h-8 text-sm text-white font-mono flex-1" data-testid="wp-base-url-input" />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveBaseUrl} disabled={savingBase || !wpBaseDraft.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 h-8" data-testid="wp-base-url-save">
                {savingBase ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setWpBaseDraft(wpBaseUrl); setEditingBase(false); }}
                className="h-8 text-slate-400" data-testid="wp-base-url-cancel">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-indigo-100 font-mono truncate" data-testid="wp-base-url-value">{wpBaseUrl || '— not set —'}</p>
            <Button size="sm" variant="outline" onClick={() => { setWpBaseDraft(wpBaseUrl); setEditingBase(true); }}
              className="h-7 text-[11px] border-indigo-600/60 text-indigo-300 hover:bg-indigo-900/30 flex-shrink-0" data-testid="wp-base-url-edit">Edit</Button>
          </div>
        )}
      </div>


      {/* ── Feedback banner ───────────────────────────────────────────── */}
      {msg && (
        <div className={`flex items-start gap-2 text-sm p-3 rounded-lg ${msg.type === 'ok' ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-700/30' : 'bg-red-900/20 text-red-400 border border-red-700/30'}`}
          data-testid="website-settings-msg">
          {msg.type === 'ok'
            ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
            : <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
          {msg.text}
        </div>
      )}

      {/* ── Section card grid ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Settings2 className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-200">Website Sections</h3>
          <span className="text-[11px] text-slate-500">Click Edit to configure a section</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECTION_DEFS.map(def => {
            // For built sections, "configured" = the corresponding payload key has any data
            const sectionData = def.built ? settings?.[def.key] : null;
            const configured = def.built && (
              Array.isArray(sectionData) ? sectionData.length > 0 : !!sectionData
            );
            return (
              <SectionCard
                key={def.key}
                def={def}
                configured={configured}
                lastSaved={def.built ? settings?.updated_at : null}
                onEdit={openSection}
                data={sectionData}
              />
            );
          })}
        </div>
      </div>

      {/* ── Edit Sheet (right-side panel) ─────────────────────────────── */}
      <Sheet open={!!activeSection} onOpenChange={open => !open && setActiveSection(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg bg-slate-900 border-slate-700 flex flex-col p-0"
          data-testid={`website-sheet-${activeSection}`}
        >
          {/* Sheet header */}
          <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-700/60 flex-shrink-0">
            <div className="flex items-center gap-2 pr-6">
              {activeDef && React.createElement(activeDef.icon, {
                className: `h-4 w-4 ${activeDef?.iconColor || 'text-slate-400'}`,
              })}
              <SheetTitle className="text-base text-white">{activeDef?.title}</SheetTitle>
            </div>
            <SheetDescription className="text-[11px] text-slate-400 mt-0.5">
              {activeDef?.description}
            </SheetDescription>
          </SheetHeader>

          {/* Scrollable sheet body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {activeSection === 'ticker'         && <TickerSheetBody        ticker={localTicker}     setT={setT} />}
            {activeSection === 'sponsors'       && <SponsorsSheetBody      sponsors={localSponsors} setS={setS} />}
            {activeSection === 'sponsor_packages' && <SponsorPackagesSheetBody val={localSponsorPackages} setVal={setLocalSponsorPackages} />}
            {activeSection === 'donation' && <DonationSheetBody val={localDonation} setVal={setLocalDonation} orgId={effectiveOrgId} />}
            {activeSection === 'hero'           && <HeroSheetBody          hero={localHero}         setH={setH} />}
            {activeSection === 'upcoming_games' && <UpcomingGamesSheetBody ug={localUg}             setU={setU} />}
            {activeSection === 'home_sections'  && <HomeSectionsSheetBody  hs={localHs}             setHS={setHS} />}
            {activeSection === 'explore_more'   && <ExploreMoreSheetBody   val={localExplore}       setVal={setLocalExplore} />}
            {activeSection === 'stats'          && <StatsSheetBody         rows={localStats}        setRows={setLocalStats} overrides={localStatsOverrides} setOverrides={setLocalStatsOverrides} orgId={effectiveOrgId} />}
            {activeSection === 'sabres_tv'      && <SabresTvSheetBody      tv={localTv}             setTv={setTv} />}
            {activeSection === 'join_cta'       && <JoinCtaSheetBody       jc={localJc}             setJ={setJ} />}
            {activeSection === 'nav'            && <NavSheetBody           nav={localNav}           setN={setN} />}
            {activeSection === 'footer'         && <FooterSheetBody        footer={localFooter}     setF={setF} setFooter={setLocalFooter} />}
            {activeSection === 'leadership'     && <LeadershipSheetBody    lead={localLeadership}   setL={setLead} />}
            {activeSection === 'about_page'     && <AboutPageSheetBody     val={localAboutPage}     setVal={setLocalAboutPage} />}
            {activeSection === 'members_page'   && <MembersPageSheetBody   val={localMembersPage}   setVal={setLocalMembersPage} />}
            {activeSection === 'standings'      && <StandingsSheetBody     standings={localStandings} setStd={setStd} />}
          </div>

          {/* Sheet footer: Save button */}
          <div className="flex-shrink-0 px-5 py-4 border-t border-slate-700/60 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm"
              className="text-slate-400 hover:text-slate-200"
              onClick={() => setActiveSection(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="website-settings-save-btn">
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <Save className="h-4 w-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
}
